<template>
    <div v-if="isLoading">
        <font-awesome-icon icon="spinner" spin/>
    </div>
    <b-overlay v-else :show="isSaving" :class="{'opacity-2': isRequestingSuggestion}">
        <h5 v-if="rerouteMode && hasReroutableDispatchRequests" class="mb-2 text-dark">Bestaande leveringen</h5>

        <div
            v-for="(dispatchRequest, index) in currentDispatchRequests" :key="dispatchRequest.id"
            v-if="!rerouteMode || dispatchRequest.isReroutable"
            :data-drq-id="dispatchRequest.id"
            class="dispatchRequest mb-4 mt-1 font-size-md p-4 card"
            :class="{
                'opacity-2': isDragging,
                'drag-not-allowed': isDragging,
            }"
            no-body
        >
            <b-modal :id="`quick-reroute-quantity-modal-${dispatchRequest.id}`"
                     :title="quickRerouteLine ? (quickRerouteLine?.product_name ?? getSalesOrderLine(quickRerouteLine.sales_order_line_id).product_name) : ''"
                     size="md"
                     centered
                     dialog-class="shadow-none"
                     @ok="quickRerouteDispatchRequestLine(dispatchRequest, quickRerouteLine, quickRerouteQuantity, quickRerouteToStatus, quickRerouteDispatchDate)"
                     :okTitle="$t('omt.gui.general.action.process')"
                     :cancelTitle="$t('omt.gui.general.action.cancel')"
            >
                <b-row>
                    <b-col cols="10">
                        Welk aantal wil je
                        <span v-if="quickRerouteToStatus === 'cancelled'">annuleren</span>
                        <span v-else-if="quickRerouteToStatus === 'unable-to-ship'">voorlopig manco melden</span>
                        <span v-else-if="quickRerouteToStatus === 'unable-to-ship-final'">definitief manco melden</span>
                        <span v-else-if="quickRerouteToStatus === 'on-hold'">blokkeren</span>
                        <span v-else-if="quickRerouteToStatus === 'to-be-requested'">later uitleveren</span>
                        <span v-else>wijzigen</span>
                        ?
                    </b-col>
                    <b-col cols="2"><b-input type="number" size="sm" :min="1" v-model="quickRerouteQuantity"/></b-col>
                </b-row>

                <b-row v-if="quickRerouteDispatchDate !== false" class="mt-2">
                    <b-col cols="8">Wat wordt de nieuwe uitleverdatum?</b-col>
                    <b-col cols="4"><b-input type="date" size="sm" :min="1" v-model="quickRerouteDispatchDate"/></b-col>
                </b-row>
            </b-modal>

            <b-modal :id="`ask-split-quantity-modal-${dispatchRequest.id}`"
                     v-model="dispatchRequest.isSplitModalOpen"
                     title="Splitsen"
                     size="md"
                     centered
                     dialog-class="shadow-none"
                     @ok="splitLine(dispatchRequest, lineToSplit, productQuantityToSplit)"
            >
                <template #default="{ ok }">
                    <form @submit.prevent="ok()">
                        <b-row>
                            <b-col cols="10">Welk aantal je opsplitsen?</b-col>
                            <b-col cols="2"><b-input type="number" size="sm" :min="1" v-model="productQuantityToSplit" autofocus/></b-col>
                        </b-row>
                    </form>
                </template>
            </b-modal>

            <b-modal :id="`ask-delay-modal-${dispatchRequest.id}`"
                     title="Uitleverdatum aanpassen"
                     size="md" centered dialog-class="shadow-none"
                     @ok="processQuickReroute({ originalDispatchRequest: dispatchRequest, toStatus: 'to-be-requested', newDispatchDate: quickRerouteDispatchDate })"
            >
                <template #default="{ ok }">
                    <form @submit.prevent="ok()">
                        <b-row>
                            <b-col cols="8">Wat wordt de nieuwe uitleverdatum?</b-col>
                            <b-col cols="4"><b-input type="date" size="sm" :min="today" v-model="quickRerouteDispatchDate" required autofocus/></b-col>
                        </b-row>
                    </form>
                </template>
            </b-modal>

            <b-modal :id="`ask-pause-modal-${dispatchRequest.id}`"
                     :title="`Levering pauzeren`"
                     size="md" centered dialog-class="shadow-none"
                     @ok="processPauseDispatchRequest(dispatchRequest)"
            >
                Weet je zeker dat je de levering wilt pauzeren?
            </b-modal>
            <b-modal :id="`ask-resume-modal-${dispatchRequest.id}`"
                     :title="`Levering hervatten`"
                     size="md" centered dialog-class="shadow-none"
                     @ok="processResumeDispatchRequest(dispatchRequest)"
            >
                Weet je zeker dat je de levering wilt hervatten?
            </b-modal>
            <b-modal :id="`ask-override-pickupcode-modal-${dispatchRequest.id}`"
                     :title="`Markeer als afgehaald`"
                     size="md" centered dialog-class="shadow-none"
                     @ok="processOverridePickupCode(dispatchRequest)"
            >
                Weet je zeker dat je de levering wilt markeren als afgehaald?
            </b-modal>

            <b-modal
              :id="`responsible-location-modal-${dispatchRequest.id}`"
              @ok="handleProcessResponsibleLocation(dispatchRequest)"
              :okTitle="$t('omt.gui.general.action.save')"
              :cancelTitle="$t('omt.gui.general.action.cancel')"
              :ok-disabled="!responsibleLocationChange.responsibleLocationId"
              :title="`${$t('omt.gui.orders.choose-responsible-location.title')}`"
              size="md"
              centered
              dialog-class="shadow-none"
              >
                <ChooseResponsibleLocation :sales-order="salesOrder" @dataChange="(...args) => responsibleLocationPopupDataChange(...args)" />
            </b-modal>

            <b-modal
              :id="`choose-dropshipper-modal-${dispatchRequest.id}`"
              @ok="handleProcessChooseDropshipper(dispatchRequest)"
              :okTitle="$t('omt.gui.general.action.save')"
              :cancelTitle="$t('omt.gui.general.action.cancel')"
              :ok-disabled="!chooseDropshipper.dispatcherIdentifier"
              title="Dropshipper kiezen"
              size="md"
              centered
              dialog-class="shadow-none"
              >
                <ChooseDropshipper :dispatchRequest="dispatchRequest" @dataChange="(...args) => chooseDropshipperPopupDataChange(...args)" />
            </b-modal>

            <b-modal :id="`change-location-modal-${dispatchRequest.id}`"
                     title="Locatie wijzigen"
                     size="md" centered dialog-class="shadow-none"
                     :ok-disabled="!isQuickRerouteLocationValid"
                     @ok="processQuickReroute({ originalDispatchRequest: dispatchRequest, newLocation: newDispatchRequestLocation, lines: dispatchRequest.dispatch_request_lines })"
            >
                <b-row class="new-dr-location mb-2">
                  <b-col cols="12">
                    Wat wordt de nieuwe locatie?
                  </b-col>

                  <b-col cols="12">
                    <template v-if="loadingQuickRerouteLocations">
                      <span class="icon mr-2">
                        <font-awesome-icon icon="spinner" spin/>
                      </span>
                      <span class="label">
                        Lijst met locaties ophalen&hellip;
                      </span>
                    </template>

                    <template v-else>
                      <span v-if="quickRerouteLocationLocations.length == 0">
                        Er zijn geen locaties beschikbaar.
                      </span>

                      <b-form-select v-else
                                     v-model="newDispatchRequestLocation.dispatcher_identifier"
                                     @change="getDispatchTypesForDispatcher(newDispatchRequestLocation.dispatcher_identifier)"
                                     size="sm">
                        <b-form-select-option :value="null">
                          Kies een locatie&hellip;
                        </b-form-select-option>

                        <b-form-select-option
                          v-for="location in quickRerouteLocationLocations"
                          :key="location.id"
                          :value="location.identifier"
                          >
                          {{ location.display_name }} ({{ location.identifier }})
                        </b-form-select-option>
                      </b-form-select>
                    </template>
                  </b-col>
                </b-row>

                <b-row class="new-dr-location-dispatch-type mb-2" v-if="newDispatchRequestLocation.dispatcher_identifier !== null">
                  <b-col cols="12">
                    Wat wordt de vervoersmethode?
                  </b-col>

                  <b-col cols="12">
                    <template v-if="loadingQuickRerouteDispatchTypes">
                      <span class="icon mr-2">
                        <font-awesome-icon icon="spinner" spin/>
                      </span>
                      <span class="label">
                        Lijst met vervoersmethoden ophalen&hellip;
                      </span>
                    </template>

                    <template v-else>
                      <span v-if="quickRerouteLocationDispatchTypes.length == 0" class="no-dispatch-types-available">
                        Geen vervoersmethoden beschikbaar voor deze locatie.
                      </span>

                      <b-form-select v-else size="sm" v-model="newDispatchRequestLocation.dispatch_type">
                        <b-form-select-option :value="null">
                          Kies een vervoersmethode&hellip;
                        </b-form-select-option>

                        <b-form-select-option
                          v-for="dispatch_type in quickRerouteLocationDispatchTypes"
                          :key="dispatch_type.id"
                          :value="dispatch_type"
                          >
                          {{ dispatch_type.display_name }}
                        </b-form-select-option>
                      </b-form-select>
                    </template>
                  </b-col>
                </b-row>

                <b-row class="new-dr-location-carrier mb-2" v-if="newDispatchRequestLocation.dispatch_type !== null">
                  <b-col cols="12">
                    Wie wordt de vervoerder?
                  </b-col>

                  <b-col cols="12">
                      <span v-if="!dispatchTypeHasActiveCarriers(newDispatchRequestLocation.dispatch_type)" class="no-dispatch-types-available">
                        <template v-if="newDispatchRequestLocation.dispatch_type.identifier === 'vendor_dropship'">
                          De leverancier zal de bestelling direct naar de klant sturen.
                        </template>

                        <template v-else>
                          Geen vervoerders beschikbaar voor deze locatie.
                        </template>
                      </span>

                      <b-row v-else>
                        <b-col cols="12">
                          <b-form-select size="sm" v-model="newDispatchRequestLocation.carrier">
                            <b-form-select-option :value="null">
                              Kies een vervoerder&hellip;
                            </b-form-select-option>
                            <b-form-select-option
                              v-for="carrier in getActiveCarriersForDispatchType(newDispatchRequestLocation.dispatch_type)"
                              :key="carrier.id"
                              :value="carrier"
                              >
                              {{ carrier.display_name }}
                            </b-form-select-option>
                          </b-form-select>
                        </b-col>
                      </b-row>
                  </b-col>
                </b-row>

                <b-row class="new-dr-location-carrier-method" v-if="newDispatchRequestLocation.carrier !== null">
                  <b-col cols="12">
                    Wat wordt de vervoersmethode?
                  </b-col>

                  <b-col cols="12">
                      <span v-if="!carrierHasActiveCarrierMethods(newDispatchRequestLocation.carrier)">
                        Geen vervoermethoden beschikbaar voor deze vervoerder.
                      </span>

                      <b-row v-else>
                        <b-col cols="12">
                          <b-form-select size="sm" v-model="newDispatchRequestLocation.carrier_method">
                            <b-form-select-option :value="null">
                              Kies een vervoersmethode&hellip;
                            </b-form-select-option>
                            <b-form-select-option
                              v-for="carrier_method in getActiveCarrierMethodsForCarrier(newDispatchRequestLocation.carrier)"
                              :key="carrier_method.id"
                              :value="carrier_method"
                              >
                              {{ carrier_method.display_name }}
                            </b-form-select-option>
                          </b-form-select>
                        </b-col>
                      </b-row>
                  </b-col>
                </b-row>
            </b-modal>

            <b-modal :id="`suggestion-for-dispatch-request-modal-${dispatchRequest.id}`"
                     v-model="dispatchRequest.isSuggestionModalOpen"
                     :key="`suggestion-for-dispatch-request-modal-${dispatchRequest.id}`"
                     size="md"
                     centered
                     dialog-class="shadow-none"
                     @ok="getSuggestionForDispatchRequest(dispatchRequest, true)"
                     title="Reroute suggestie vragen"
            >
                <b-row>
                    <b-col cols="10">
                        Wat is het maximaal aantal leveringen?
                    </b-col>
                    <b-col cols="2"><b-input type="number" size="sm" :min="1" v-model="rerouteMaxDispatchRequests"/></b-col>
                </b-row>
                <b-row class="mt-4">
                    <b-col cols="10 pt-1">
                        Sluit eerder gebruikte locaties voor {{ $store.state.config.settings.excludePreviousLocationsForHourCount }} uur uit:
                    </b-col>
                    <b-col cols="2"><b-checkbox type="number" size="lg" switch v-model="rerouteSuggestionExcludePreviousLocations"/></b-col>
                </b-row>
            </b-modal>

            <b-modal :id="`drq-line-history-modal-${dispatchRequest.id}`"
                     :title="`Leveringsstatus-geschiedenis van artikel ${dispatchRequestHistoryLine?.product_identifier} - ${dispatchRequestHistoryLine?.product_name ?? getSalesOrderLine(dispatchRequestHistoryLine?.sales_order_line_id).product_name}`"
                     size="xl" centered dialog-class="shadow-none" hide-footer
            >
                <dispatch-request-history v-if="dispatchRequestHistoryLine" :dispatch-request-line="dispatchRequestHistoryLine" :sales-order="salesOrder" :key="dispatchRequestHistoryLine?.id"/>
            </b-modal>

            <b-modal :id="`switch-carrier-method-modal-${dispatchRequest.id}`"
                     title="Wijzig vervoersmethode"
                     size="md" centered dialog-class="shadow-none"
                     @ok="handleSwitchCarrier(dispatchRequest)"
            >
                <switch-carrier-method :dispatch-request="dispatchRequest" @dataChange="(...args) => switchCarrierDataChange(...args)"/>
            </b-modal>

            <div :data-drq-id="dispatchRequest.id">
                <BRow>
                    <BCol cols="12 p-0">
                        <b-alert :show="dispatchRequest.paused_at" variant="warning" class="mb-2">
                            <strong><font-awesome-icon icon="pause-circle" class="text-danger font-size-xlg align-middle mr-2"/></strong>
                            Deze levering is gepauzeerd! Er zullen geen updates plaatsvinden totdat deze weer wordt hervat.
                        </b-alert>
                        <b-alert variant="warning" class="mb-2" :show="hasAssignMessages(dispatchRequest)">
                            <font-awesome-icon icon="bullhorn" class="text-info font-size-xlg align-middle mr-2"/>

                            <span v-if="hasAssignMessages(dispatchRequest)">
                                <strong v-if="dispatchRequest.status === 'unable-to-ship'">Afwijsreden:</strong>
                                <strong v-else>HARR Redenen:</strong>

                                <ul class="mb-0" :class="{'mb-2': dispatchRequest.is_dispatch_assign_retryable}">
                                    <li v-for="assignComponentResult in relevantAssignMessages(dispatchRequest)">
                                        {{ getAssignComponentResultMessage(assignComponentResult) }}
                                    </li>
                                    <li v-if="getUnableToShipReasonForHarr(dispatchRequest)">
                                        {{ getUnableToShipReasonForHarr(dispatchRequest) }}
                                    </li>
                                </ul>
                            </span>
                            <small v-if="dispatchRequest.is_dispatch_assign_retryable">
                                <i>Deze levering wordt opnieuw geprobeerd om automatisch toe te wijzen na: {{ dispatchRequest.reassign_at | datetimeshort }}.</i>
                            </small>
                        </b-alert>
                    </BCol>
                </BRow>
                <BRow :class="showInformationHeadings ? 'mb-4' : 'mb-0'">
                    <BCol cols="12" md="3" class="py-2">
                        <BCardTitle :class="{ 'text-danger': dispatchRequest.status === 'cancelled' }">
                            <span v-b-tooltip="dispatchRequest.status_reason" class="text-uppercase">
                                <span class="icon">
                                    <DispatchRequestIcon :status="dispatchRequest.status" />&nbsp;
                                </span>
                                <span v-if="dispatchRequest.is_replaced && !showReplacedAsNormal" class="icon">
                                    <DispatchRequestIcon status="rerouted"/>&nbsp;
                                </span>
                                <span class="drq-status-label" :data-original-status="dispatchRequest.status"
                                      :class="{'text-strikethrough': getStrikeThrough(dispatchRequest)}"
                                >
                                    {{ dispatchRequestStatus(dispatchRequest.status, dispatchRequest.dispatch_type.identifier) }}
                                    <small v-if="dispatchRequest.status_reason"><font-awesome-icon icon="circle-info" class="text-secondary d-inline-block ml-1"/></small>
                                </span>
                            </span>
                        </BCardTitle>
                    </BCol>

                    <BCol cols="9">
                        <span v-if="isResponsibleLocationChanged(dispatchRequest)" class="d-inline-block ml-4 text-danger text-uppercase font-weight-bold float-right">
                            {{ $t('omt.gui.orders.dispatch-requests.is-responsible-location-changed-message') }}
                        </span>
                        <OmniButtonCollection v-else-if="!hideActions && !dispatchRequest.is_replaced && !isRerouted(dispatchRequest)" class="justify-content-end">
                            <OmniButton
                                v-if="canQuickRerouteToLocation(dispatchRequest)"
                                @click="changeDispatchRequestLocation(dispatchRequest)"
                                icon="fas fa-map-marked-alt"

                            >
                                Wijs toe aan winkel
                            </OmniButton>

                            <OmniButton
                                v-if="!rerouteMode && dispatchRequest.isPausable"
                                @click="pauseDispatchRequest(dispatchRequest)"
                                icon="pause-circle"
                            >
                                Pauzeer deze levering
                            </OmniButton>

                            <OmniButton
                                v-if="!rerouteMode && dispatchRequest.isUnpausable"
                                @click="resumeDispatchRequest(dispatchRequest)"
                                icon="play-circle"
                            >
                                Resumeer deze levering
                            </OmniButton>


                            <OmniButton v-if="!rerouteMode && dispatchRequest.isResponsibleLocationChangable"
                                        @click="openChooseResponsibleLocationModal(dispatchRequest)"
                                        icon="user-tie"
                            >
                                {{ $t('omt.gui.orders.search.filters.label.responsible-store-identifier')}} omzetten
                            </OmniButton>

                            <OmniButton v-if="!rerouteMode && dispatchRequest.isChooseDropshipperVisible"
                                        @click="openChooseDropshipperModal(dispatchRequest)"
                                        icon="parachute-box"
                            >
                                Producten dropshippen
                            </OmniButton>

                            <OmniButton v-if="!rerouteMode && dispatchRequest.isDelayable"
                                        @click="delayDispatchRequest(dispatchRequest, 'to-be-requested')"
                                        icon="calendar"
                            >
                                Leverdatum aanpassen
                            </OmniButton>

                            <OmniButton v-if="!rerouteMode && dispatchRequest.isSwitchCarrierAvailable"
                                        @click="openSwitchCarrierModal(dispatchRequest)"
                                        icon="truck"
                            >
                                Vervoersmethode wijzigen
                            </OmniButton>

                            <download-packing-slip-button v-if="showDispatchRequestDeliveryFields(dispatchRequest)"
                                                          :dispatch-request="dispatchRequest"
                                                          :always-show="dispatchRequest.dispatcher_type === 'dc' && hasPermission('orders.dispatchrequests.downloadpackingslip')"
                                                          size="md"
                            >
                                Download pakbon en label
                            </download-packing-slip-button>

                            <download-packing-slip-button v-if="isExtraLabelButtonVisible(dispatchRequest)"
                                                          :dispatch-request="dispatchRequest"
                                                          :ignore-cache="true"
                                                          :ask-label-count="true"
                                                          :always-show="true"
                                                          size="md"
                            >
                                    Extra labels aanvragen
                            </download-packing-slip-button>

                            <print-labels-button v-if="showDispatchRequestDeliveryFields(dispatchRequest)"
                                                 :dispatch-request="dispatchRequest"
                                                 size="md"
                            />

                            <OmniButton v-if="hasPermission('orders.dispatchrequests.showcascades') && dispatchRequest.cascades_into_dispatch_requests?.length"
                                        @click="openCascadedDispatchRequestsModal(dispatchRequest)"
                                        icon="link"
                            >
                                    Toon gelinkte leveringen
                            </OmniButton>

                            <OmniButton v-if="isOrderPickByStoreScannable(dispatchRequest)"
                                        variant="outline-primary" size="md" icon="barcode"
                                        :to="{name: 'OrderPickByStoreScan', params: {salesOrderIdentifier: salesOrder.identifier, dispatchRequestId: dispatchRequest.id}}"
                            >
                                Pick deze levering
                            </OmniButton>

                            <OmniButton v-if="hasPermission('orders.dispatchrequests.createreturn')"
                                        @click="openReturnDrqItemsModal(dispatchRequest)"
                                        variant="link"
                                        icon="reply"
                            >
                                Artikelen retourneren
                            </OmniButton>

                            <OmniButton v-if="!dispatchRequest.dispatch_request_batch_id && hasPermission('orders.batch.create') && dispatchRequest.status === 'to-be-requested'"
                                        :disabled="isCreatingBatch"
                                        :is-processing="isCreatingBatch"
                                        @click="handleCreateBatch(dispatchRequest)"
                                        icon="box-open"
                            >
                                Batch aanmaken
                            </OmniButton>

                            <OmniButton v-if="dispatchRequest.dispatch_request_batch_id && hasPermission('orders.batch.process.sort')"
                                        :to="{
                                        name: 'ProcessStartWithBatch',
                                        params: {
                                            batchModelType: 'shipment-batch',
                                            processType: 'sort',
                                            locationTypeIdentifier: dispatchRequest.dispatcher_type,
                                            locationIdentifier: dispatchRequest.dispatcher_identifier,
                                            batchModelIdentifier: dispatchRequest.dispatch_request_batch.identifier
                                        }
                                    }"
                                    icon="layer-group"
                            >
                                Batch sorteren
                            </OmniButton>

                            <OmniButton v-if="!rerouteMode && dispatchRequest.isHoldable"
                                        @click="quickRerouteDispatchRequest(dispatchRequest, 'on-hold')"
                                        variant="danger"
                                        icon="truck"
                            >
                                    Levering blokkeren
                            </OmniButton>

                            <OmniButton v-if="!rerouteMode && dispatchRequest.isCancelable"
                                        @click="quickRerouteDispatchRequest(dispatchRequest, 'cancelled')"
                                        variant="danger"
                                        icon="ban"
                            >
                                Levering annuleren
                            </OmniButton>

                            <OmniButton v-if="!rerouteMode && dispatchRequest.isPickupCodeOverridable"
                                        @click="overridePickupCode(dispatchRequest)"
                                        icon="user-check"
                            >
                                Markeer als afgehaald
                            </OmniButton>

                            <OmniButton v-if="!rerouteMode && dispatchRequest.isShippable"
                                        @click="quickRerouteDispatchRequest(dispatchRequest, 'shipped')"
                                        variant="success"
                                        icon="truck"
                            >
                                Markeer als verzonden
                            </OmniButton>
                        </OmniButtonCollection>

                        <OmniButtonCollection v-if="$scopedSlots.extraActions" class="justify-content-end">
                            <slot name="extraActions" :dispatchRequest="dispatchRequest"/>
                        </OmniButtonCollection>

                        <OmniButtonCollection v-if="rerouteMode" class="justify-content-end">
                            <OmniButton
                                v-if="hasPermission('orders.dispatchrequests.suggestionbased.reroute') && rerouteMode && dispatchRequest.isReroutable"
                                @click="toggleSuggestionForDispatchRequestModal(dispatchRequest)"
                            >
                                <template #icon>
                                    <FontAwesomeIcon v-if="!isRequestingSuggestion" icon="user-graduate"/>
                                    <FontAwesomeIcon v-if="isRequestingSuggestion" icon="spinner" :spin="isRequestingSuggestion"/>
                                </template>

                                Een reroute suggestie vragen
                            </OmniButton>
                        </OmniButtonCollection>
                    </BCol>
                </BRow>

                <BCol cols="12">
                    <BRow style="font-size: .85rem; gap: 1.5rem 4rem;" class="d-flex flex-wrap">
                        <span v-if="dispatchRequest.responsible_location && showDrqInformationBlock('responsible_location')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                     {{ $t('omt.gui.orders.label.responsible-location') }}
                                    </strong>
                                </span>

                                <span>
                                    <span v-if="!showLocationLink(dispatchRequest.responsible_location?.location_type.identifier, dispatchRequest.responsible_location?.identifier)">{{ dispatchRequest.responsible_location.display_name }}</span>
                                    <b-link v-else :to="{
                                            name: 'EditLocationServices',
                                            params: {
                                                locationTypeIdentifier: dispatchRequest.responsible_location?.location_type.identifier,
                                                locationIdentifier: dispatchRequest.responsible_location?.identifier,
                                            }}" style="color:inherit;"
                                            title="Bekijk locatie"
                                    >{{ dispatchRequest.responsible_location.display_name }}</b-link>
                                </span>
                            </div>
                        </span>

                        <span v-if="showDrqInformationBlock('dispatcher_identifier')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                        {{ $t('omt.gui.orders.label.location') }}
                                    </strong>
                                </span>
                                <span>
                                    <span v-if="!showLocationLink(dispatchRequest.dispatcher_type, dispatchRequest.dispatcher_identifier)">{{ getDispatcherName(dispatchRequest) }}</span>
                                    <b-link v-else :to="{
                                        name: 'EditLocationServices',
                                        params: {
                                            locationTypeIdentifier: dispatchRequest.dispatcher_type,
                                            locationIdentifier: dispatchRequest.dispatcher_identifier,
                                        }}" style="color:inherit;"
                                        title="Bekijk locatie"
                                    >{{ getDispatcherName(dispatchRequest) }}</b-link>
                              </span>
                            </div>
                        </span>

                        <span v-if="showDrqInformationBlock('location_type_identifier')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                        {{ $t('omt.gui.orders.label.location-type') }}
                                    </strong>
                                </span>

                                <span>
                                    {{ getDispatcherType(dispatchRequest) }}
                                </span>
                            </div>
                        </span>

                        <span v-if="showDispatchFields(dispatchRequest) && showDrqInformationBlock('dispatch_type')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                        Type levering
                                    </strong>
                                </span>
                                <span>
                                    {{ dispatchRequest.dispatch_type.display_name }} {{ dispatchRequest.metadata?.auto_purchase ? '(Automatisch)' : '' }}
                                </span>
                            </div>
                        </span>

                      <span v-if="showBoxSuggestion(dispatchRequest) && showDrqInformationBlock('suggested_box')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                        Doosadvies
                                    </strong>
                                </span>

                                <span v-if="dispatchRequest.box_suggestion" :title="!showInformationHeadings ? 'Doosadvies' : false">
                                    {{ dispatchRequest.box_suggestion?.suggested_box?.identifier }}
                                </span>
                            </div>
                        </span>

                        <span v-if="showDispatchFields(dispatchRequest) && showDispatchRequestDeliveryFields(dispatchRequest) && showDrqInformationBlock('carrier_identifier')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                        Bezorging door
                                    </strong>
                                </span>
                                <span class="d-flex align-items-center" style="gap: .25rem;">
                                    <carrier-icon v-if="dispatchRequest.carrier" :carrier="dispatchRequest.carrier.identifier" class="carrier-icon d-inline-block align-middle mr-1" />
                    {{ dispatchRequest.carrier_method ? dispatchRequest.carrier_method.display_name : 'Vervoersmethode onbekend' }}
                                </span>
                            </div>
                        </span>

                        <span v-if="showDispatchRequestDeliveryFields(dispatchRequest) && hasPermission('orders.dispatchrequests.showdatefield.dispatchdate') && showDrqInformationBlock('dispatch_date')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                        Uitleverdatum
                                    </strong>
                                </span>
                                <span>
                                    <span v-if="dispatchRequest.dispatch_date" :title="!showInformationHeadings ? 'Uitleverdatum' : false">{{ dispatchRequest.dispatch_date | date }}</span>
                                    <span v-else>onbekend</span>
                                </span>
                            </div>
                        </span>

                        <span v-if="hasPermission('orders.dispatchrequests.showdatefield.updatedat') && showDrqInformationBlock('updated_at')">
                            <div class="d-flex flex-column">
                                <span v-if="showInformationHeadings">
                                    <strong class="drq-info-heading">
                                        Laatste update
                                    </strong>
                                </span>

                                <span :title="!showInformationHeadings ? 'Laatste update' : false">
                                    {{ dispatchRequest.updated_at | datetime }}
                                </span>
                            </div>
                        </span>
                    </BRow>
                </BCol>
            </div>

            <b-card-body class="pl-0" body-bg-variant="default">
                <b-table-simple stacked="sm" small borderless class="mb-0">
                    <colgroup>
                        <col v-if="rerouteMode && dispatchRequest.isReroutable" style="width:2rem;">
                        <col style="width:7rem;"/>
                        <col style="width:10rem;"/>
                        <col style="width:auto;"/>
                        <col v-if="!rerouteMode" style="width:12rem;"/>
                        <col v-if="!rerouteMode" style="width:8rem;"/>
                        <col v-if="showFinancialAmounts" style="width:7rem;"/>
                        <col style="width:14rem;"/>
                    </colgroup>

                    <b-thead>
                        <b-tr>
                            <b-th v-if="rerouteMode && dispatchRequest.isReroutable"></b-th>
                            <b-th>Aantal</b-th>
                            <b-th>Artikel nr</b-th>
                            <b-th>Naam</b-th>
                            <b-th v-if="!rerouteMode && showFinancialAmounts">Prijs totaal (ex. korting)</b-th>
                            <b-th v-if="!rerouteMode && showFinancialAmounts">Korting</b-th>
                            <b-th v-if="showFinancialAmounts">Prijs totaal</b-th>
                            <b-th>Extra informatie</b-th>
                        </b-tr>
                    </b-thead>

                    <draggable v-if="rerouteMode && dispatchRequest.isReroutable"
                               tag="tbody" v-model="dispatchRequest.dispatch_request_lines"
                               group="productsInDispatchRequest"
                               :forceFallback="true /* Required for cursor change to work */"
                               @end="productMoved"
                               @start="isDragging = true"
                               @unchoose="isDragging = false"
                               ghost-class="ghost-product"
                               :data-dispatch-request-id="dispatchRequest.id"
                               data-is-existing-dispatch-request="1"
                    >
                        <b-tr v-for="(line, index) in dispatchRequest.dispatch_request_lines" :key="`${line.id}_${index}`"
                              class="draggable"
                              :class="{
                                'drag-not-allowed': isDragging,
                              }"
                        >
                            <b-td v-if="rerouteMode && dispatchRequest.isReroutable" class="text-secondary draggable">
                                <font-awesome-icon icon="grip-vertical" class="text-info"/>
                            </b-td>
                            <b-td stacked-heading="Aantal">
                                {{ line.quantity }}
                                <b-button v-if="line.quantity > 1" variant="link" size="sm" title="Splitsen" class="text-primary p-0 btn-split-drq"
                                          @click="startSplitLine(dispatchRequest, line)"
                                >
                                    <font-awesome-icon icon="share-alt"/>
                                </b-button>
                            </b-td>
                            <b-td stacked-heading="Artikel nr">
                                <product-number :number="line.product_identifier" :dispatch-request-line="line" :sales-order-line="getSalesOrderLine(line.sales_order_line_id)"/>
                            </b-td>
                            <b-td stacked-heading="Naam">{{ line.product_name ?? getSalesOrderLine(line.sales_order_line_id).product_name }}</b-td>
                            <b-td v-if="showFinancialAmounts" stacked-heading="Prijs totaal">
                                {{ getTotalPriceToShow(line, true) | price }}
                                <small v-if="hasOriginalExtraOrderLineUnitPrice(line)" class="mr-2">
                                    <font-awesome-icon icon="circle-info" title="Nalevering, de getoonde prijs is die van de originele regel" class="ml-2 text-info align-text-top"/>
                                </small>
                            </b-td>
                            <b-td stacked-heading="Extra info">
                                <small v-if="shouldShowProductInsufficientStock(dispatchRequest, line.product_identifier)" class="text-danger">
                                    <font-awesome-icon icon="triangle-exclamation" class="text-warning"/>
                                    Onvoldoende voorraad<br />
                                </small>
                                <span v-if="dispatchRequest.storage_location_id && dispatchRequest.status === 'requested'">
                                    <i>Gescand: {{ line.final_scanned_quantity }}</i>
                                </span>
                            </b-td>
                        </b-tr>
                    </draggable>

                    <b-tbody v-else>
                        <b-tr v-for="line in dispatchRequest.dispatch_request_lines" :key="line.id" :data-line-id="line.id">
                            <b-td stacked-heading="Aantal">
                                {{ line.quantity }}
                                <span v-if="!hideActions">
                                    <b-button v-if="dispatchRequest.isDelayable" size="sm" variant="link" class="text-danger ml-1 m-0 p-0" title="Dit product later uitleveren"
                                              @click="startQuickRerouteDispatchRequestLine(dispatchRequest, line, 'to-be-requested', true)"
                                    >
                                        <small class="align-text-bottom text-dark"><font-awesome-icon icon="far fa-calendar" class="align-text-top"/></small>
                                    </b-button>
                                    <b-button v-if="dispatchRequest.isCancelable" size="sm" variant="link" class="text-danger ml-1 m-0 p-0" title="Dit product annuleren"
                                              @click="startQuickRerouteDispatchRequestLine(dispatchRequest, line, 'cancelled')"
                                    >
                                        <small class="align-text-bottom"><font-awesome-icon icon="ban" class="align-text-top"/></small>
                                    </b-button>
                                    <b-button v-if="dispatchRequest.isUnableToShipable" size="sm" variant="link" class="text-warning ml-1 m-0 p-0" title="Dit product op manco zetten"
                                          @click="startQuickRerouteDispatchRequestLine(dispatchRequest, line, 'unable-to-ship')"
                                    >
                                        <small class="align-text-bottom"><font-awesome-icon icon="truck" flip="horizontal" class="align-text-top" /></small>
                                    </b-button>
                                    <b-button v-if="dispatchRequest.isUnableToShipFinalable" size="sm" variant="link" class="text-danger ml-1 m-0 p-0" title="Dit product definitief op manco zetten"
                                                  @click="startQuickRerouteDispatchRequestLine(dispatchRequest, line, 'unable-to-ship-final')"
                                    >
                                        <small class="align-text-bottom"><font-awesome-icon icon="truck" flip="horizontal" class="align-text-top" /></small>
                                    </b-button>
                                </span>
                            </b-td>
                            <b-td stacked-heading="Artikel nr">
                                <product-number :number="line.product_identifier" :dispatch-request-line="line" :sales-order-line="getSalesOrderLine(line.sales_order_line_id)"/>
                                <b-button v-if="hasPermission('orders.dispatchrequests.history')" @click="showDrqLineHistory(line)"
                                          size="sm" variant="link" class="text-secondary ml-1 m-0 p-0" title="Toon leveringsstatus-geschiedenis van dit product"
                                >
                                    <small class="align-text-bottom"><font-awesome-icon icon="eye" class="align-text-top"/></small>
                                </b-button>

                                <OmniDropdownIcon
                                    :cardItems="cardItems"
                                    :cardItemVariables="getArticleHoverCardVariables(line)"
                                    class="ml-2"
                                />
                            </b-td>
                            <b-td stacked-heading="Naam">{{ line.product_name ?? getSalesOrderLine(line.sales_order_line_id).product_name }}</b-td>

                            <b-td v-if="showFinancialAmounts && hasOriginalExtraOrderLineUnitPrice(line)" stacked-heading="Prijs totaal (ex. korting)"></b-td>
                            <b-td v-else-if="showFinancialAmounts" stacked-heading="Prijs totaal (ex. korting)">{{ getTotalPriceToShow(line, false) | price }}</b-td>

                            <b-td v-if="showFinancialAmounts && hasOriginalExtraOrderLineUnitPrice(line)" stacked-heading="Korting"></b-td>
                          <b-td v-else-if="showFinancialAmounts" stacked-heading="Korting">{{ getDiscountPriceToShow(line) | price }}</b-td>

                            <b-td v-if="showFinancialAmounts" stacked-heading="Prijs totaal">
                                {{ getTotalPriceToShow(line, true) | price }}
                                <small v-if="hasOriginalExtraOrderLineUnitPrice(line)" class="mr-2">
                                    <font-awesome-icon icon="circle-info" title="Nalevering, de getoonde prijs is die van de originele regel" class="ml-2 text-info align-text-top"/>
                                </small>
                            </b-td>

                            <b-td stacked-heading="Extra informatie">
                                <small v-if="shouldShowProductInsufficientStock(dispatchRequest, line.product_identifier)" class="text-danger">
                                    <font-awesome-icon icon="triangle-exclamation" class="text-warning"/>
                                    Onvoldoende voorraad<br />
                                </small>

                                <small v-if="showDispatchRequestDeliveryFields(dispatchRequest)">
                                    <span v-if="line.presales_active?.length">Presale<br /></span>
                                    <span v-else-if="salesOrder.promised_delivery_date">Beloofde leverdatum: {{ salesOrder.promised_delivery_date | date }}<br /></span>
                                </small>

                                <small v-if="getSalesOrderLine(line.sales_order_line_id)?.metadata?.private_copy_levy"> <!-- Expert specific! -->
                                    Thuiskopieheffing: {{ (getSalesOrderLine(line.sales_order_line_id)?.metadata?.private_copy_levy * line.quantity) | price }}<br/>
                                </small>

                                <small v-if="dispatchRequest.storage_location_id && dispatchRequest.status === 'requested'">
                                    <i>Gescand: {{ line.final_scanned_quantity }}</i>
                                </small>

                                <p v-if="getSalesOrderLine(line.sales_order_line_id)?.promotions?.length" class="m-0">
                                    <small style="text-decoration: underline dotted;cursor:default" class="text-primary" :id="`promotions_${line.id}`">
                                        {{ getSalesOrderLine(line.sales_order_line_id).promotions.length }} promotie<span v-if="getSalesOrderLine(line.sales_order_line_id).promotions.length > 1">s</span>
                                    </small>

                                    <b-popover :target="`promotions_${line.id}`" triggers="hover" placement="auto" no-fade delay="0" custom-class="promotion-popover">
                                        <promotion-lines
                                            :promotion-lines="getSalesOrderLine(line.sales_order_line_id).promotions"
                                            :sales-order-line="getSalesOrderLine(line.sales_order_line_id)"
                                            :dispatch-request-line="line"
                                            style="background: white;"/>
                                    </b-popover>
                                </p>

                                <small v-if="dispatchRequest.status.startsWith('return-')">
                                    <strong class="drq-info-heading">Retourreden:</strong> {{ getReturnReason(line.return_reason_id).display_name ?? 'onbekend' }}<br />
                                </small>
                                <small v-if="dispatchRequest.status === 'return-received'">
                                    <strong class="drq-info-heading">Staat:</strong> {{ getProductState(line.product_state_id).display_name ?? 'onbekend'}}<br />
                                </small>

                                <small v-if="line.goods_receipt_line_id">
                                    In goederenontvangst:
                                    <b-link v-if="hasLocationId(line.goods_receipt_line?.goods_receipt.recipient_location_id)"
                                            :to="getGoodsReceiptViewLink(line.goods_receipt_line.goods_receipt)">{{ line.goods_receipt_line.goods_receipt?.identifier }}</b-link>
                                    <span v-else>{{ line.goods_receipt_line.goods_receipt?.identifier }}</span>
                                    <br />
                                </small>
                            </b-td>
                        </b-tr>
                    </b-tbody>

                </b-table-simple>
            </b-card-body>

            <b-card-footer  v-if="dispatchRequest.tracking_codes?.length ||
                            (!rerouteMode && dispatchRequest.dispatch_request_batch_id && hasPermission('orders.batch.process.sort')) ||
                            dispatchRequest.storage_location_id"
                            footer-bg-variant="light"
                            footer-border-variant="light"
            >
                <b-row class="w-100">
                    <b-col cols="12" md="4" class="pr-4 text-black-50 mb-4 mb-md-0">
                        <span v-if="dispatchRequest.tracking_codes.length">
                            Track &amp; trace:
                            <a v-for="(trackingCode, index) in dispatchRequest.tracking_codes" :href="trackingCode.url" class="text-info d-inline-block mx-1" target="_blank">[{{datetime(trackingCode.updated_at)}}]</a>
                        </span>
                    </b-col>
                    <b-col v-if="!rerouteMode" cols="12" md="4" class="text-black-50">
                        <span v-if="dispatchRequest.dispatch_request_batch_id && hasPermission('orders.batch.process.sort')">
                            <strong class="drq-info-heading">In batch: </strong>
                            <b-link :to="{name: 'ShipmentBatchView',
                                      params: {
                                        locationTypeIdentifier: dispatchRequest.dispatcher_type,
                                        locationIdentifier: dispatchRequest.dispatcher_identifier,
                                        batchIdentifier: dispatchRequest.dispatch_request_batch.identifier,
                                      }}"
                            >
                                {{dispatchRequest.dispatch_request_batch.identifier}}
                            </b-link>
                            <br />
                        </span>
                        <span v-if="dispatchRequest.storage_location_id">
                            <strong class="drq-info-heading">In sorteerzone:</strong> {{dispatchRequest.storage_location?.storage_zone.display_name}} &rarr; {{dispatchRequest.storage_location?.display_name}}
                        </span>
                        <br/>
                    </b-col>
                </b-row>
            </b-card-footer>
        </div>
        <b-alert :show="rerouteMode && !hasReroutableDispatchRequests" variant="light">
            Er zijn geen leveringen met een status die toegestaan is om te rerouten.
        </b-alert>

        <!-- ================================= CREATE NEW DISPATCH REQUEST ======================================== -->

        <h5 v-if="rerouteMode && hasReroutableDispatchRequests" class="mb-2 text-dark">Nieuwe leveringen</h5>

        <div v-if="rerouteMode && hasReroutableDispatchRequests">
            <!-- This second collapse is only so newly added drq's appear nicely -->
            <b-alert :show="!newDispatchRequests.length" variant="light" class="px-0 pt-1 pb-4">
                <span v-if="isLoadingAllLocations">
                    <font-awesome-icon icon="spinner" spin/> Bezig met laden dispatch opties
                </span>
                <span v-else>Voeg een lege levering toe, of maak een levering aan met de waardes van een bestaande levering.</span>
            </b-alert>
            <b-collapse :visible="true" :appear="true"
                        v-for="(dispatchRequest, index) in newDispatchRequests" :key="index"
            >
                <b-card class="dispatchRequest mb-4 font-size-md" no-body
                        :class="{'opacity-05': !rerouteMode}"
                >
                    <b-card-header header-bg-variant="light">
                        <b-col cols="2" class="p-0">
                            <h6 class="mb-0">
                                <span :title="dispatchRequest.status"><dispatch-request-icon :status="dispatchRequest.status" class="mr-2"/></span>
                                <strong class="drq-info-heading">{{ dispatchRequest.status | dispatchRequestStatus | uppercase }}</strong>
                            </h6>
                        </b-col>
                        <b-col cols="3" class="text-black-50">
                            <b-row>
                                <b-col cols="5" class="px-0 py-1 text-right">
                                    Uitleverdatum:
                                </b-col>
                                <b-col cols="7" class="px-1">
                                    <b-input v-model="dispatchRequest.dispatch_date" type="date" size="sm" :min="today"
                                             :class="{'input-not-valid': dispatchRequest.dispatch_date < today}"
                                    />
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col cols="6" class="text-black-50 pl-5">
                            <b-row>
                                <b-col cols="3" class="p-0 pl-1">
                                    <b-select v-model="dispatchRequest.dispatcher_type" size="sm" required :class="{'input-not-valid': !dispatchRequest.dispatcher_type}" readonly>
                                        <b-select-option value="" disabled class="text-secondary">Locatietype</b-select-option>
                                        <b-select-option v-for="dispatcherType in $store.state.locationDataTypes.locationTypes" :value="dispatcherType.identifier" :key="dispatcherType.identifier">
                                            {{ dispatcherType.display_name }}
                                        </b-select-option>
                                    </b-select>
                                </b-col>
                                <b-col cols="5" class="p-0 pl-1">
                                    <b-select v-model="dispatchRequest.dispatcher_identifier" size="sm" :disabled="!dispatchRequest.dispatcher_type" :class="{'input-not-valid': !dispatchRequest.dispatcher_identifier && dispatchRequest.dispatcher_type}"
                                              @change="dispatcherChanged(dispatchRequest)"
                                    >
                                        <b-select-option value="" disabled>Locatie</b-select-option>
                                        <b-select-option v-for="dispatcher in dispatchersForSelectedDispatcherType(dispatchRequest, salesOrder.sales_channel_identifier)" :value="dispatcher.identifier" :key="dispatcher.identifier">{{ dispatcher.display_name }}
                                        </b-select-option>
                                    </b-select>
                                </b-col>
                                <b-col cols="4" class="p-0 pl-1">
                                    <b-overlay :show="isLoadingDispatcherTypes" spinner-small>
                                        <b-select size="sm" :key="dispatchRequest.dispatcher_identifier" v-model="dispatchRequest.dispatch_type_identifier" :disabled="!dispatchRequest.dispatcher_identifier && !dispatcherInfo.length"
                                                  :class="{'input-not-valid': dispatchRequest.dispatcher_identifier && !dispatchRequest.dispatch_type_identifier}" @change="dispatchTypeChanged(dispatchRequest)"
                                        >
                                            <b-select-option value="" disabled>Dispatchtype</b-select-option>
                                            <b-select-option v-for="type in dispatchTypesForSelectedDispatcher(dispatchRequest)" :value="type.identifier" :key="type.identifier">{{ type.display_name }}</b-select-option>
                                        </b-select>
                                    </b-overlay>
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col cols="1" class="text-right pr-0">
                            <b-modal :id="`suggestion-for-manual-dispatch-request-modal`"
                                     v-model="dispatchRequest.isSuggestionModalOpen"
                                     :key="`suggestion-for-manual-dispatch-request-modal`"
                                     size="md"
                                     centered
                                     dialog-class="shadow-none"
                                     @ok="getSuggestionForDispatchRequest(dispatchRequest, true)"
                                     title="Locatie suggestie vragen"
                            >
                                <b-row>
                                    <b-col cols="10">
                                        Wat is het maximaal aantal leveringen?
                                    </b-col>
                                    <b-col cols="2"><b-input type="number" size="sm" :min="1" v-model="rerouteMaxDispatchRequests"/></b-col>
                                </b-row>
                                <b-row class="mt-4">
                                    <b-col cols="10 pt-1">
                                        Sluit eerder gebruikte locaties uit:
                                    </b-col>
                                    <b-col cols="2"><b-checkbox type="number" size="lg" switch v-model="rerouteSuggestionExcludePreviousLocations"/></b-col>
                                </b-row>
                            </b-modal>

                            <b-button variant="outline-secondary" size="sm"
                                      v-if="hasPermission('orders.dispatchrequests.suggestionbased.reroute') && rerouteMode"
                                      title="Een locatie suggestie vragen"
                                      @click="toggleSuggestionForDispatchRequestModal(dispatchRequest)"
                                      :disabled="dispatchRequest.dispatch_request_lines.length === 0"
                            >
                                <small class="text-dark">
                                    <font-awesome-icon v-if="!isRequestingSuggestion" icon="user-graduate" class="align-text-top"/>
                                    <font-awesome-icon v-if="isRequestingSuggestion" icon="spinner" :spin="isRequestingSuggestion" class="align-text-top"/>
                                </small>
                            </b-button>
                            <b-button variant="outline-danger" size="sm" class="m-1"
                                      :disabled="!canNewDispatchRequestBeRemoved(index)"
                                      :title="canNewDispatchRequestBeRemoved(index) ? 'Verwijderen' : 'Kan niet verwijderen' "
                                      @click="removeNewDispatchRequest(index)">
                                <font-awesome-icon icon="trash"/>
                            </b-button>
                        </b-col>
                    </b-card-header>
                    <b-card-body>

                        <b-table-simple class="ml-2 my-4 b" small borderless>
                            <colgroup>
                                <col style="width:2rem;"/>
                                <col style="width:7rem;"/>
                                <col style="width:10rem;"/>
                                <col style="width:auto;"/>
                                <col style="width:7rem;"/>
                                <col style="width:14rem;"/>
                            </colgroup>
                            <b-thead>
                                <b-tr>
                                    <b-th></b-th>
                                    <b-th>Aantal</b-th>
                                    <b-th>Artikel nr</b-th>
                                    <b-th>Naam</b-th>
                                    <b-th>Prijs totaal</b-th>
                                    <b-th>Extra informatie</b-th>
                                </b-tr>
                            </b-thead>

                            <draggable tag="tbody" v-model="dispatchRequest.dispatch_request_lines"
                                       group="productsInDispatchRequest"
                                       :forceFallback="true /* Required for cursor change to work */"
                                       :move="productMoving"
                                       @end="productMoved"
                                       @start="isDragging = true"
                                       @unchoose="isDragging = false"
                                       :data-dispatch-request-id="dispatchRequest.id"
                                       data-is-existing-dispatch-request="0"
                            >
                                <b-tr v-for="(line, index) in dispatchRequest.dispatch_request_lines" :key="`${line.id}_${index}`"
                                      class="draggable"
                                      :title="line.gui.isInvalidlyPlacedReason"
                                >
                                    <b-td class="text-secondary draggable" style="cursor:grab;">
                                        <font-awesome-icon icon="grip-vertical" class="text-info"/>
                                    </b-td>
                                    <b-td>
                                        {{ line.quantity }}
                                        <!-- Splitting merged lines is not possible because of the complexity when identical products from different drq's are put into the same new-drq, merged, and then split again -->
                                        <!-- This could lead to incorrect quantities of products with their corresponding old_line_id -->
                                        <b-button v-if="line.quantity > 1"
                                                  title="Splitsen"
                                                  :class="{'text-primary': 1}"
                                                  class="p-0"
                                                  variant="link"
                                                  size="sm"
                                                  @click="startSplitLine(dispatchRequest, line)"
                                        >
                                            <font-awesome-icon icon="share-alt"/>
                                        </b-button>
                                    </b-td>
                                    <b-td>
                                        <product-number :number="line.product_identifier" :dispatch-request-line="line" :sales-order-line="getSalesOrderLine(line.sales_order_line_id)"/>
                                        <font-awesome-icon v-if="line.gui.isInvalidlyPlaced" icon="exclamation-triangle" class="text-danger cursor-default"/>
                                    </b-td>
                                    <b-td>{{ line.product_name ?? getSalesOrderLine(line.sales_order_line_id).product_name }}</b-td>
                                    <b-td>
                                        {{ getTotalPriceToShow(line, true) | price }}
                                        <small v-if="hasOriginalExtraOrderLineUnitPrice(line)" class="mr-2">
                                            <font-awesome-icon icon="circle-info" title="Nalevering, de getoonde prijs is die van de originele regel" class="ml-2 text-info align-text-top"/>
                                        </small>
                                    </b-td>
                                    <b-td></b-td>
                                </b-tr>
                            </draggable>

                            <b-tr>
                                <td></td>
                                <td colspan="5" class="pt-2">
                                    <span class="text-secondary font-italic">
                                        Sleep orderregels hier naartoe
                                    </span>
                                </td>
                            </b-tr>

                            <b-tr>
                                <b-td></b-td>
                                <b-td class="pt-3">
                                    <strong>Totaal aantal</strong>
                                </b-td>
                                <b-td></b-td>
                                <b-td></b-td>
                                <b-td colspan="2" class="pt-3">
                                    <strong>Totaal prijs levering</strong>
                                </b-td>
                            </b-tr>
                            <b-tr>
                                <b-td class="text-secondary draggable" style="cursor:grab;">

                                </b-td>
                                <b-td>
                                    {{ getTotalQuantityForDrq(dispatchRequest.dispatch_request_lines) }}
                                </b-td>
                                <b-td>

                                </b-td>
                                <b-td>

                                </b-td>
                                <b-td>
                                    {{ getTotalPriceToShowForDrq(dispatchRequest.dispatch_request_lines) | price }}
                                </b-td>
                                <b-td></b-td>
                            </b-tr>
                        </b-table-simple>
                    </b-card-body>
                    <b-card-footer footer-bg-variant="light" footer-border-variant="light">
                        <b-col cols="2" class="px-0">
                            <b-overlay :show="isLoadingDispatcherTypes" spinner-small>
                                <b-select size="sm" v-model="dispatchRequest.carrier_identifier" :disabled="!dispatchRequest.dispatch_type_identifier"
                                          :class="{'input-not-valid': dispatchRequest.dispatch_type_identifier && !dispatchRequest.carrier_identifier}"
                                          :key="dispatchRequest.dispatcher_identifier" @change="carrierChanged(dispatchRequest)"
                                >
                                    <b-select-option value="" disabled>Vervoerder</b-select-option>
                                    <b-select-option v-for="carrier in carriersForSelectedDispatcherAndDispatchType(dispatchRequest)" :value="carrier.identifier" :key="carrier.identifier">
                                        {{ carrier.display_name }}
                                    </b-select-option>
                                </b-select>
                            </b-overlay>
                        </b-col>
                        <b-col cols="2" class="pl-1">
                            <b-overlay :show="isLoadingDispatcherTypes" spinner-small>
                                <b-select size="sm" v-model="dispatchRequest.carrier_method_identifier" :disabled="!dispatchRequest.carrier_identifier"
                                          :class="{'input-not-valid': !dispatchRequest.carrier_method_identifier && dispatchRequest.dispatch_type_identifier}"
                                          :key="dispatchRequest.dispatcher_identifier"
                                >
                                    <b-select-option value="" disabled>Vervoersmethode</b-select-option>
                                    <b-select-option v-for="carrierMethod in carrierMethodsForSelectedCarrierAndDispatcherAndDispatchType(dispatchRequest)" :value="carrierMethod.identifier" :key="carrierMethod.identifier">
                                        {{ carrierMethod.display_name }}
                                    </b-select-option>
                                </b-select>
                            </b-overlay>
                        </b-col>
                    </b-card-footer>
                </b-card>
            </b-collapse>

            <div class="text-right">
                <b-button variant="outline-primary" size="sm" @click="unsplitAllLines" class="ml-2">Optimaliseren</b-button>

                <b-button variant="primary" size="sm" @click="addDispatchRequest(newDispatchRequest)" class="ml-2"
                          :disabled="isLoadingAllLocations"
                >
                    + Nieuwe toewijzing
                    <font-awesome-icon v-if="isLoadingAllLocations" icon="spinner" spin/>
                </b-button>

                <b-button variant="success" size="sm" class="ml-2" :disabled="!canReroutingBeSaved" @click="save">
                    <font-awesome-icon v-if="isSaving" icon="spinner" spin/>
                    <span v-else>Opslaan</span>
                </b-button>
            </div>
        </div>

        <b-modal id="cascaded-drqs-modal"
                 :title="`Gelinkte leveringen`"
                 size="xl" centered dialog-class="shadow-none" hide-footer
        >
            <dispatch-requests :sales-order="salesOrder" :dispatch-requests="showCascadedDispatchRequests" :hide-actions="true" />
        </b-modal>

        <b-modal id="return-drq-items-modal"
                 :title="`Artikelen retourneren`"
                 size="xl" centered dialog-class="shadow-none" hide-footer
        >
            <ReturnDrqItems
                :dispatch-request="returnDrq"
                :sales-order="salesOrder"
                @close="$bvModal.hide('return-drq-items-modal')"
                @dataChanged="$emit('dataChanged')"
            />
        </b-modal>
    </b-overlay>
</template>

<script>
import Draggable from "vuedraggable";
import DispatchRequestIcon from "../../../components/DispatchRequestIcon";
import CarrierIcon from "../../../components/CarrierIcon";
import DownloadPackingSlipButton from "./DownloadPackingSlipButton";
import dispatchRequestReturnStatuses from "../../../data/dispatchRequestReturnStatuses";
import moment from "moment";
import GenericHelpers from "../../../helpers/generic-helpers";
import * as DispatchHelper from "../../../helpers/dispatch";
import PromotionLines from "./PromotionLines.vue";
import DispatchRequestHistory from "./DispatchRequestHistory.vue";
import {DispatchRequestsBase} from "./dispatchRequestsBase";
import ChooseResponsibleLocation from "./modals/ChooseResponsibleLocation.vue";
import ShowDispatchRequestMetadata from "./modals/ShowDispatchRequestMetadata.vue";
import ReturnDrqItems from './modals/ReturnDrqItems.vue';
import ChooseDropshipper from "./modals/ChooseDropshipper.vue";
import OmniButtonCollection    from '@/components/OmniStock/OmniButtonCollection.vue';
import OmniButton              from '@/components/OmniStock/OmniButton.vue';
import {checkCascadingDispatchRequestsHaveNoOtherStatuses} from "../../../helpers/dispatch-requests";
import {getLocationForUserById, hasLocation, hasLocationId} from "../../../helpers/permissions";
import LocationLink from "../../../components/LocationLink.vue";
import {datetime} from "@/helpers/date";
import ProductNumber from "../../../components/ProductNumber.vue";
import PrintLabelsButton from "@/views/OrderDetail/components/PrintLabelsButton.vue";
import {
    carrierMethodsForSelectedCarrierAndDispatcherAndDispatchType,
    carriersForSelectedDispatcherAndDispatchType,
    dispatchRequestStatus,
    dispatchTypesForSelectedDispatcher
} from "../../../helpers/dispatch";
import SwitchCarrierMethod from "@/views/OrderDetail/components/modals/SwitchCarrierMethod.vue";

export default {
    name: "DispatchRequests",

    extends: DispatchRequestsBase,

    components: {
        SwitchCarrierMethod,
        PrintLabelsButton,
        ProductNumber,
        LocationLink,
        ChooseDropshipper,
        CarrierIcon,
        DispatchRequestHistory,
        DispatchRequestIcon,
        DownloadPackingSlipButton,
        Draggable,
        PromotionLines,
        ChooseResponsibleLocation,
        ShowDispatchRequestMetadata,
        OmniButtonCollection,
        OmniButton,
        ReturnDrqItems,
    },

    props: {
        dispatchRequests: {required: true},
        salesOrder: {required: true},
        rerouteMode: {default: false},
        hideActions: {default: false},
        showReplacedAsNormal: {default: false},
    },

    data() {
        return {
            isLoading: true,
            isLoadingAllLocations: true,
            isSaving: false,
            isExporting: false,
            isDragging: false,
            isLoadingDispatcherTypes: false,
            isRequestingSuggestion: false,
            isCreatingBatch: false,
            relevantLocations: [],
            isResumingInsteadOfPausing: false,

            /**
             * Quick Reroute to Location
             */
            loadingQuickRerouteLocations:      true,
            loadingQuickRerouteDispatchTypes:  true,
            loadingQuickRerouteCarriers:       true,

            quickRerouteLocationLocations:     [],
            quickRerouteLocationDispatchTypes: [],

            newDispatchRequestLocation: {
              dispatcher_type:            'store', // Defaults to store, can be extended in the future.
              dispatcher_identifier:      null,
              dispatch_type:              null,
              carrier:                    null,
              carrier_method:             null,
            },

            dispatcherInfo: [],
            currentDispatchRequests: [],
            cascadedDispatchRequests: [],
            showCascadedDispatchRequests: [],
            newDispatchRequests: [],
            newDispatchRequest: {
                "status": "",
                "dispatcher_type": "",
                "dispatcher_identifier": "",
                "dispatch_date": "",
                "delivery_date": "",
                "carrier_identifier": "",
                "carrier_method_identifier": "",
                "dispatch_type_identifier": "",
                "dispatch_request_lines": [],
                "isSuggestionModalOpen": false,
            },
            dragToId: null, // Allows draggable instances to know when an item is dragged to it
            quickRerouteLine: null,
            quickRerouteToStatus: null,
            quickRerouteQuantity: 1,
            quickRerouteDispatchDate: null,
            lineToSplit: null,
            productQuantityToSplit: 1,
            dispatchRequestHistoryLine: null,
            showResponsibleLocationModal: false,
            rerouteMaxDispatchRequests: 1,
            rerouteSuggestionExcludePreviousLocations: true,
            showSuggestionForDispatchRequestModal: false,
            responsibleLocationChange: {
                responsibleLocationId: null,
                dispatcherType: null,
                dispatcherIdentifier: null,
                dispatchType: null,
                carrier: null,
                carrierMethod: null,
            },
            chooseDropshipper: {
                dispatcherType: null,
                dispatcherIdentifier: null,
                dispatchType: null,
                carrier: null,
                carrierMethod: null,
                deliveryDate: null,
            },
            switchCarrier: {
                carrierMethodId: null,
            },

            returnDrq: {},
        }
    },

    computed: {
        hasReroutableDispatchRequests() {
            return !!this.currentDispatchRequests.some(dr => dr.isReroutable)
        },
        canReroutingBeSaved() {
            return !this.newDispatchRequests
                .map(dr => {
                    return dr.dispatch_request_lines.length !== 0
                        && !dr.dispatch_request_lines.filter(line => line.gui.isInvalidlyPlaced).length
                        && dr.status
                        && dr.dispatcher_type
                        && dr.dispatcher_identifier
                        && dr.dispatch_date >= this.today
                        && dr.carrier_identifier
                        && dr.carrier_method_identifier
                        && dr.dispatch_type_identifier
                })
                .filter(canBeSaved => !canBeSaved)
                .length
        },

        /**
         * (Computed property) Checks the validity of the options for the 'Quick Reroute to Location' form.
         * This is used to enable/disable the 'Ok' button of the quick reroute modal.
         * If the dispatch type is 'vendor_dropship', the carrier is not required, because a dropship
         * is always delivered by the vendor. In all other cases, all fields are required.
         * Limitations: It does not check if the values are actually valid, only whether they are set or not.
         */
        isQuickRerouteLocationValid() {
          if (this.newDispatchRequestLocation.dispatch_type === null) {
            return false;
          }

          switch(this.newDispatchRequestLocation.dispatch_type.identifier) {
            case 'vendor_dropship':
              return this.newDispatchRequestLocation.dispatch_type.identifier   !== null &&
                     this.newDispatchRequestLocation.dispatcher_identifier      !== null ;
            default:
              return this.newDispatchRequestLocation.dispatch_type.identifier   !== null &&
                     this.newDispatchRequestLocation.dispatcher_identifier      !== null &&
                     this.newDispatchRequestLocation.carrier_method             !== null &&
                     this.newDispatchRequestLocation.carrier                    !== null ;
          }
        },

        /** Begin IAM-based GUI section
         *   The following computed properties are being used to show, hide or alter certain parts
         *   of the GUI, based on the Identity & Access Management settings.
         */
        showFinancialAmounts() {
            return this.hasPermission('orders.dispatchrequests.showfinancialamounts');
        },
        /** END IAM-based GUI section */

        /** Begin Frontend Parameter section */

        cardItems() {
            return [
                ...(this.$store.getters.getFrontendParameterValue('order-details', 'hover-menu-items') ?? []),
                ...((this.isFeatureEnabled('stock-management') && this.hasAnyPermission(['stockmanagement.view']))
                    ? [{
                        label: 'Naar voorraadpagina',
                        value: `${this.$router.resolve({name: 'StockManagement'})?.href}/{article_no}`,
                        openInCurrentTab: true,
                    }]
                    : []
                ),
                ...((this.hasPermission('sle.quicksuggestion'))
                    ? [{
                        label: 'Doe snelle voorraadcheck',
                        value: `${this.$router.resolve({name: 'QuickSuggestion'})?.href}/{article_no}`,
                        openInCurrentTab: true,
                    }]
                    : []
                ),
            ]
        },

        showInformationHeadings() {
            return this.$store.getters.getFrontendParameterValue('order-details', 'drq-show-information-headings') ?? true;
        },

        /** End Frontend Parameter section */

        pausedOrResumeText() {
            return this.isResumingInsteadOfPausing
                ? 'hervatten'
                : 'pauzeren'
        },
    },

    mounted() {
        let preloadPromises = [
            this.$store.getters.getLocationsForUser,
            this.$store.getters.getDispatchRequestAllowedStatusChanges,
            this.$store.getters.getLocationDataTypes,
        ]

        // Preload some data that's only needed when there are return-* DRQ's
        if (this.dispatchRequests.some(dr => Object.values(dispatchRequestReturnStatuses).indexOf(dr.status) !== -1)) {
            preloadPromises = preloadPromises.concat([
                this.$store.getters.getProductStates,
                this.$store.getters.getReturnReasons,
            ])
        }

        // Preload data
        Promise.all(preloadPromises)
            .then(() => {
                _.each(this.currentDispatchRequests, dr => {
                    dr.isReroutable = this.$store.state.dispatchRequestAllowedStatusChanges[dr.status]?.length
                        && this.$store.state.dispatchRequestAllowedStatusChanges[dr.status].indexOf('to-be-requested') !== -1
                        && this.hasPermission('orders.dispatchrequests.reroute')
                        && !dr.paused_at
                    dr.isDelayable = dr.status === 'to-be-requested'
                        && this.hasPermission('orders.dispatchrequests.delay')
                        && !dr.paused_at
                    dr.isCancelable = this.$store.getters.getFromStatusesForRerouteToStatus('cancelled').indexOf(dr.status) !== -1
                        && this.hasPermission('orders.dispatchrequests.cancel')
                        && checkCascadingDispatchRequestsHaveNoOtherStatuses(
                            dr,
                            this.cascadedDispatchRequests,
                            this.$store.getters.getFromStatusesForRerouteToStatus('cancelled', true)
                        )
                        && !dr.paused_at
                    dr.isUnableToShipable = this.$store.getters.getFromStatusesForRerouteToStatus('unable-to-ship').indexOf(dr.status) !== -1
                        && this.hasPermission('orders.dispatchrequests.unabletoship')
                        && !dr.paused_at
                    dr.isUnableToShipFinalable = this.$store.getters.getFromStatusesForRerouteToStatus('unable-to-ship-final').indexOf(dr.status) !== -1
                        && this.hasPermission('orders.dispatchrequests.unabletoshipfinal')
                        && !dr.paused_at
                    dr.isHoldable = this.$store.getters.getFromStatusesForRerouteToStatus('on-hold').indexOf(dr.status) !== -1
                        && this.hasPermission('orders.dispatchrequests.hold')
                        && !dr.paused_at
                    dr.isPausable = this.hasPermission('orders.dispatchrequests.pause')
                        && !dr.paused_at
                        && this.$store.getters.getPausableStatuses(dr.dispatcher_type).indexOf(dr.status) !== -1
                    dr.isUnpausable = this.hasPermission('orders.dispatchrequests.pause')
                        && dr.paused_at
                    dr.isResponsibleLocationChangable = this.showDispatchRequestDeliveryFields(dr)
                        && this.hasPermission('orders.dispatchrequests.responsiblelocation.edit')
                        && this.$store.getters.getFromStatusesForRerouteToStatus('to-be-requested').indexOf(dr.status) !== -1
                        && !this.currentDispatchRequests.some(drq => drq.status === 'shipped-with-issues')
                    dr.isChooseDropshipperVisible = dr.status === 'to-be-requested'
                        && this.hasPermission('orders.dispatchrequests.choosedropshipper')
                        && !dr.paused_at
                    dr.isExportable = this.hasPermission('orders.dispatchrequests.export')
                    dr.isShippable = this.hasPermission('orders.dispatchrequests.markasshipped')
                        && !dr.dispatch_request_lines.some(drl => drl.quantity_pending > 0)
                    dr.isSwitchCarrierAvailable = this.hasPermission('orders.dispatchrequests.changecarrier')
                        && ['to-be-requested', 'requested'].includes(dr.status)
                    dr.isPickupCodeOverridable = dr.status === 'ready-to-ship'
                        && this.hasPermission('orders.pickupcode.override')
                })

                // Load all locations when rerouting, or only relevant locations when only showing existing drq's
                let locationsPromise = null
                if (this.rerouteMode || this.$store.state.allLocations.length) {
                    locationsPromise = this.$store.getters.getAllLocations
                        .then(() => this.isLoadingAllLocations = false)
                }
                else {
                    const query = GenericHelpers.toQueryString({
                        type_and_identifier: this.dispatchRequests.map(dr => {
                            return `${DispatchHelper.dispatcherType(dr.dispatcher_type).id},${dr.dispatcher_identifier}`
                        }),
                    })

                    locationsPromise = this.$http.get(`/api/rest/locations?${query}`)
                        .then(response => {
                            this.relevantLocations = response.data
                        })
                }

                locationsPromise.then(() => {
                    if (this.rerouteMode) {
                        this.addDispatchRequest(this.newDispatchRequest)
                    }
                    this.isLoading = false
                    this.$emit('loaded', this.hasReroutableDispatchRequests)
                })
            })

        let dispatchRequests = _.cloneDeep(this.dispatchRequests)
            .map(dr => {
                dr.isSplitModalOpen = false
                dr.isSuggestionModalOpen = false
                dr.dispatch_request_lines = dr.dispatch_request_lines.map(line => {
                    line.gui = {
                        originalStatus: dr.status,
                        isInvalidlyPlaced: false,
                        isInvalidlyPlacedReason: '',
                        old_line_ids: [],
                    }

                    // Give old_line_ids the same amount of id's as there is quantity. That way we can split more easily later on
                    for (let i = 0; i < line.quantity; i++) {
                        line.gui.old_line_ids.push(line.id)
                    }

                    return line
                })
                return dr
            })


        this.cascadedDispatchRequests = dispatchRequests.reduce(
            (reduction, drq) => (drq.cascades_into_dispatch_requests?.length) ? reduction.concat(drq.cascades_into_dispatch_requests) : reduction,
            [],
        )

        this.currentDispatchRequests = dispatchRequests
            ?.filter(drq => !this.cascadedDispatchRequests.some(drq2 => drq2.id === drq.id))
            ?.filter(drq =>
                this.hasLocation(drq.dispatcher_type, drq.dispatcher_identifier)
                || this.hasLocation(drq.responsible_location?.location_type.identifier, drq.responsible_location?.identifier)
                || this.$store.state.locationsForUser.some(l => l.id === drq.responsible_location_id)
                || (drq.is_replaced === 0 && this.hasPermission('orders.dispatchrequests.include.reroutedreplaceddispatchrequests'))
            ) ?? []

        const drqSortOrder = this.$store.getters.getFrontendParameterValue('dispatch-requests', 'status-display-order');

        // Create a copy of the original array with indices
        const currentDrqsWithIndex = this.currentDispatchRequests.map((item, index) => ({ item, index }));
        this.currentDispatchRequests = currentDrqsWithIndex.sort((a, b) => {
            const indexA = drqSortOrder.indexOf(a.item.status);
            const indexB = drqSortOrder.indexOf(b.item.status);

            if (indexA === -1 && indexB === -1) {
                // If both statuses are not in drqSortOrder, sort by original position
                return a.index - b.index;
            } else if (indexA === -1) {
                return 1; // push a to the end
            } else if (indexB === -1) {
                return -1; // push b to the end
            } else {
                return indexA - indexB; // sort by drqSortOrder
            }
        }).map(el => el.item);

        if (!this.currentDispatchRequests.length) {
            this.currentDispatchRequests = dispatchRequests[1] ?? []
        }
    },

    methods: {
        dispatchRequestStatus,
        datetime,
        dispatchTypesForSelectedDispatcher,
        carriersForSelectedDispatcherAndDispatchType,
        carrierMethodsForSelectedCarrierAndDispatcherAndDispatchType,
        productMoving(event) {
            let dragTo = event.relatedContext.component.$el
            this.dragToId = dragTo.getAttribute('data-dispatch-request-id')

            if (dragTo.getAttribute('data-is-existing-dispatch-request') === "1") {
                return false
            }
        },
        productMoved() {
            this.dragToId = null
        },
        addDispatchRequest(dispatchRequest) {
            let productCount = _.sum(this.currentDispatchRequests.map(dr => {
                return (dr.isReroutable || dr.isCancelable || dr.isUnableToShipable || dr.isDelayable || dr.isHoldable)
                    ? _.sumBy(dr.dispatch_request_lines, line => line.quantity)
                    : 0
            }))

            productCount += _.sum(this.newDispatchRequests.map(dr => {
                return _.sumBy(dr.dispatch_request_lines, line => line.quantity)
            }))

            if (productCount <= this.newDispatchRequests.length) {
                this.$alert('Er kunnen niet meer leveringen gemaakt worden dan er producten zijn.', 'info')
                return
            }

            let newDispatchRequest = _.cloneDeep(this.newDispatchRequest)
            newDispatchRequest.status = 'to-be-requested'
            if (dispatchRequest.id) {
                newDispatchRequest.dispatcher_type = dispatchRequest.dispatcher_type
                newDispatchRequest.dispatcher_identifier = dispatchRequest.dispatcher_identifier
                newDispatchRequest.dispatch_type_identifier = dispatchRequest.dispatch_type.identifier
                newDispatchRequest.dispatch_date = dispatchRequest.dispatch_date
                newDispatchRequest.carrier_identifier = dispatchRequest.carrier?.identifier
                newDispatchRequest.carrier_method_identifier = dispatchRequest.carrier_method?.identifier
            }
            else {
                newDispatchRequest.dispatch_date = moment().format('YYYY-MM-DD')
            }

            this.newDispatchRequests.push(newDispatchRequest)
            return newDispatchRequest;
        },
        canNewDispatchRequestBeRemoved(index) {
            return !this.newDispatchRequests[index].dispatch_request_lines.length
        },
        removeNewDispatchRequest(index) {
            if (!this.canNewDispatchRequestBeRemoved(index)) {
                this.$alert('Deze levering kan niet worden verwijderd deze nog producten bevat.', 'warning')
                return
            }

            this.newDispatchRequests.splice(index, 1)
        },
        getNewDispatchRequest() {
            let newDispatchRequest = null
            for (let i = 0; i < this.newDispatchRequests.length; i++) {
                if (this.newDispatchRequests[i].dispatch_request_lines.length === 0) {
                    newDispatchRequest = this.newDispatchRequests[i]
                    break
                }
            }
            if (newDispatchRequest === null) {
                newDispatchRequest = this.addDispatchRequest(this.newDispatchRequest)
            }
            return newDispatchRequest
        },
        getSuggestionForDispatchRequest(dispatchRequest, createNewDispatchRequests) {
            let postData = dispatchRequest.dispatch_request_lines
                .map(drl => {
                    return {
                        'product_identifier': drl.product_identifier,
                        'quantity': drl.quantity
                    }
                })

            this.isRequestingSuggestion = true

            this.$http.post(`/api/suggestion/sales-order/${this.salesOrder.identifier}`, {
                'exclude_previous_locations': this.rerouteSuggestionExcludePreviousLocations,
                'lines': postData,
                'max_parts': this.rerouteMaxDispatchRequests
            })
                .then(response => {
                    if (response.data.dispatch_info.length === 0) {
                        this.$alert('Geen suggestie ontvangen', 'warning');
                        return;
                    }
                    for(let i = 0; i < response.data.dispatch_info.length; i++) {
                        let dispatchInfo = response.data.dispatch_info[i];

                        let newDispatchRequest = createNewDispatchRequests
                            ? this.getNewDispatchRequest()
                            : dispatchRequest;

                        this.dispatchRequests.push(newDispatchRequest);
                        newDispatchRequest.dispatcher_identifier = dispatchInfo.dispatcher_identifier
                        newDispatchRequest.dispatcher_type = dispatchInfo.dispatcher_type
                        newDispatchRequest.dispatch_date = dispatchInfo.dispatch_date
                        this.dispatcherChanged(newDispatchRequest)

                        for (let j = 0; j < dispatchInfo.assigned_lines.length; j++) {
                            this.moveDispatchRequestLine(
                                dispatchRequest,
                                newDispatchRequest,
                                dispatchInfo.assigned_lines[j].product_identifier,
                                dispatchInfo.assigned_lines[j].quantity
                            )
                        }

                        if (dispatchRequest.dispatch_request_lines.length === 0) {
                            break;
                        }
                    }
                    this.newDispatchRequests =  this.newDispatchRequests.filter((dispatchRequest) => dispatchRequest.dispatch_request_lines.length > 0);

                    if (this.rerouteMaxDispatchRequests < response.data.dispatch_info.length) {
                        this.$alert(`Je wilt maximaal ${this.rerouteMaxDispatchRequests} leveringen, maar deze combinatie van producten heeft minimaal ${response.data.dispatch_info.length} leveringen nodig`, 'warning', false)
                    } else if (response.data.is_fully_assigned)
                        this.$alert('Suggestie ontvangen', 'success')
                    else
                        this.$alert('Suggestie ontvangen voor een deel van de levering', 'warning', false)
                })
                .catch(e => {
                    if (!e.response) {
                        this.$alert("Er is iets misgegaan bij het verwerken van de suggestie", 'error')
                        return;
                    }
                    let message = '';
                    if (e.response.status === 500) {
                        this.$alert("Er is iets misgegaan bij het opvragen van de suggestie", 'error')
                        return;
                    }

                    switch (e.response.data?.errors?.[0]?.identifier) {
                        case 'no_stock_for_products':
                            message = 'Er is geen voorraad van de producten in de levering'
                            break
                        default:
                            message = `Geen suggestie ontvangen. Voeg 1 of meerdere producten toe aan een nieuwe levering en vraag om een locatie suggestie`
                    }
                    this.$alert(message, 'warning')
                })
                .then(() => {
                    this.isRequestingSuggestion = false
                })
        },
        moveDispatchRequestLine(originalDispatchRequest, newDispatchRequest, productIdentifier, quantity) {
            let i;
            let originalDispatchRequestLine = null
            for (i = 0; i < originalDispatchRequest.dispatch_request_lines.length; i++) {
                if (originalDispatchRequest.dispatch_request_lines[i].product_identifier === productIdentifier) {
                    originalDispatchRequestLine = originalDispatchRequest.dispatch_request_lines[i];
                    break;
                }
            }

            if (originalDispatchRequestLine === null) {
                return;
            }

            if (originalDispatchRequestLine.quantity === quantity) {
                newDispatchRequest.dispatch_request_lines.push(originalDispatchRequestLine)
                originalDispatchRequest.dispatch_request_lines.splice(i, 1)
            } else {
                this.splitLine(newDispatchRequest, originalDispatchRequestLine, quantity);
            }
        },
        startSplitLine(dispatchRequest, line) {
            if (line.quantity === 2) {
                return this.splitLine(dispatchRequest, line, 1)
            } else {
                this.lineToSplit = line
                dispatchRequest.isSplitModalOpen = true
                // this.$refs[`splitInput_${dispatchRequest.id}`][0].focus()
            }
        },
        splitLine(dispatchRequest, line, splitQuantity) {
            if (isNaN(splitQuantity) || splitQuantity < 1 || splitQuantity > line.quantity - 1) {
                return this.$alert(`Voer een getal in tussen de 1 en de ${line.quantity - 1}.`, "danger")
            }

            let splitLine = _.cloneDeep(line)

            line.quantity -= splitQuantity
            splitLine.quantity = parseInt(splitQuantity)

            splitLine.gui.old_line_ids = []
            for (let i = 0; i < splitQuantity; i++) {
                splitLine.gui.old_line_ids.push(line.gui.old_line_ids.pop())
            }

            dispatchRequest.dispatch_request_lines.push(splitLine)
        },
        unsplitAllLines(event, silent = false) {
            let actuallyDidSomething = this.unsplitLinesForDispatchRequests(this.currentDispatchRequests)
                || this.unsplitLinesForDispatchRequests(this.newDispatchRequests)

            if (!silent) {
                (actuallyDidSomething)
                    ? this.$alert('Gesplitste producten binnen dezelfde levering zijn samengevoegd.', 'success')
                    : this.$alert('Niets om te optimaliseren.', 'info')
            }
        },
        unsplitLinesForDispatchRequests(dispatchRequests) {
            let actuallyDidSomething = false
            _.each(dispatchRequests, dr => {
                let newLines = []
                let groupedLines = _.groupBy(dr.dispatch_request_lines, line => line.product_identifier)

                _.each(groupedLines, lines => {
                    if (lines.length === 1) {
                        newLines.push(lines[0])
                        return
                    }

                    lines[0].quantity = _.sumBy(lines, line => line.quantity)

                    // Put all unique old_line_ids into one array
                    for (let i = 1; i < lines.length; i++) {
                        lines[0].old_line_ids = _.concat(lines[0].old_line_ids, lines[i].old_line_ids)
                    }

                    newLines.push(lines[0])
                    actuallyDidSomething = true
                })

                dr.dispatch_request_lines = newLines
            })

            return actuallyDidSomething
        },
        cancelChanges() {
            this.$emit('cancelRerouteMode')
        },
        async save(event = null, displayAction = 'rerouting') {
            this.isSaving = true

            let postData = _.cloneDeep(this.newDispatchRequests).map(dr => {
                dr.lines = dr.dispatch_request_lines.map(line => {
                    return {
                        old_line_ids: _.uniq(line.gui.old_line_ids),
                        product_identifier: line.product_identifier,
                        quantity: line.quantity,
                    }
                })

                delete dr.dispatch_request_lines
                return dr
            })

            return this.$http.post(`/api/rest/dispatch-requests`, postData)
                .then(response => {
                    if (displayAction) {
                        this.$alert(`De ${displayAction} is succesvol verwerkt.`)
                    }
                    this.$emit('reroutingSuccessful')
                })
                .catch(e => {
                    if (displayAction) {
                        this.$alert(`Fout bij het verwerken van de ${displayAction}.`, 'danger')
                    }

                    throw e
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        getQuickRerouteTexts(toStatus) {
            let messageAction, title = ''
            switch (toStatus) {
                case 'cancelled':
                    title = 'Annuleren'
                    messageAction = 'wilt annuleren?'
                    break
                case 'unable-to-ship':
                    title = 'Manco melden'
                    messageAction = 'als manco wilt melden?'
                    break
                case 'unable-to-ship-final':
                    title = 'Manco melden'
                    messageAction = 'definitief als manco wilt melden?'
                    break
                case 'on-hold':
                    title = 'Blokkeren'
                    messageAction = 'wilt blokkeren?'
                    break
                case 'to-be-requested':
                    title = 'Uitleverdatum aanpassen'
                    messageAction = 'later wilt uitleveren?'
                    break
                case 'requested-paused':
                    title = 'Pauzeren'
                    messageAction = 'wilt pauzeren?'
                    break
                case 'requested':
                    title = 'Resumeren'
                    messageAction = 'wilt resumeren?'
                    break
                case 'shipped':
                    title = 'Markeer als verzonden'
                    messageAction = 'wilt markeren als verzonden?'
                    break
                default:
                    title = 'Wijzigen'
                    messageAction = 'wilt wijzigen?'
                    break
            }

            return {
                title: title,
                messageAction: messageAction,
            }
        },
        quickRerouteDispatchRequest(dispatchRequest, toStatus) {
            const texts = this.getQuickRerouteTexts(toStatus)
            this.$bvModal.msgBoxConfirm(`Weet je zeker dat je deze levering ${texts.messageAction}`, {
                title: texts.title,
                centered: true,
                dialogClass: "shadow-none",
                okTitle: this.$t('omt.gui.general.label.yes'),
                cancelTitle: this.$t('omt.gui.general.label.no'),
            })
                .then(confirmed => {
                    if (confirmed) {
                        this.processQuickReroute({ originalDispatchRequest: dispatchRequest, toStatus })
                    }
                })
        },
        delayDispatchRequest(dispatchRequest) {
            this.quickRerouteDispatchDate = dispatchRequest.dispatch_date
            this.$bvModal.show(`ask-delay-modal-${dispatchRequest.id}`)
        },
        pauseDispatchRequest(dispatchRequest) {
            this.$bvModal.show(`ask-pause-modal-${dispatchRequest.id}`)
        },
        resumeDispatchRequest(dispatchRequest) {
            this.$bvModal.show(`ask-resume-modal-${dispatchRequest.id}`)
        },
        overridePickupCode(dispatchRequest) {
            this.$bvModal.show(`ask-override-pickupcode-modal-${dispatchRequest.id}`)
        },

        changeDispatchRequestLocation(dispatchRequest) {
          this.loadingQuickRerouteLocations = true;

          this.$http.get(`/api/rest/locations`)
        .then(response => {
                this.quickRerouteLocationLocations = response.data.reduce((acc, location) => {
                    if (location.active === false) {
                        return acc;
                    }

                    if (location.location_type.identifier === 'store') {
                        acc[location.id] = location;
                    }

                    return acc;
                }, {});

                this.quickRerouteLocationLocations = Object.values(this.quickRerouteLocationLocations).sort((a, b) => {
                    return a.display_name.localeCompare(b.display_name);
                });
            })
            .catch((error) => {
                this.$alert('Er is een fout opgetreden bij het ophalen van de locaties.', 'error');
                console.error(error);
            })
            .finally(() => {
              this.loadingQuickRerouteLocations = false;
            })

            // reset obj properties of this.newDispatchRequestLocation to null
            this.newDispatchRequestLocation.dispatcher_identifier = null;
            this.newDispatchRequestLocation.dispatch_type         = null;
            this.newDispatchRequestLocation.carrier               = null;
            this.newDispatchRequestLocation.carrier_method        = null;

            this.$bvModal.show(`change-location-modal-${dispatchRequest.id}`)
        },

        startQuickRerouteDispatchRequestLine(dispatchRequest, line, toStatus, delay = false) {
            if (!delay && line.quantity === 1) {
                return this.quickRerouteDispatchRequestLine(dispatchRequest, line, 1, toStatus)
            }

            this.quickRerouteQuantity = (delay) ? line.quantity : 1
            this.quickRerouteLine = line
            this.quickRerouteToStatus = toStatus
            this.quickRerouteDispatchDate = (delay) ? dispatchRequest.dispatch_date : false

            this.$bvModal.show(`quick-reroute-quantity-modal-${dispatchRequest.id}`)
        },

        quickRerouteDispatchRequestLine(dispatchRequest, line, quantity, toStatus, newDispatchDate) {
            if (isNaN(quantity) || quantity < 1 || quantity > line.quantity) {
                this.$alert(`Voer een getal in tussen de 1 en de ${line.quantity}`, 'danger', true)
                return
            }

            const texts = this.getQuickRerouteTexts(toStatus)
            this.$bvModal.msgBoxConfirm(`Weet je zeker dat je ${quantity} stuks van '${line.product_name ?? this.getSalesOrderLine(line.sales_order_line_id).product_name}' ${texts.messageAction}`, {
                title: texts.title,
                centered: true,
                dialogClass: "shadow-none",
                size: 'lg',
                okTitle: this.$t('omt.gui.general.label.yes'),
                cancelTitle: this.$t('omt.gui.general.label.no'),
            })
                .then(confirmed => {
                    if (confirmed) {
                        let newLine = _.clone(line)
                        newLine.quantity = quantity
                        this.processQuickRerouteForLines({ originalDispatchRequest: dispatchRequest, lines: [newLine], toStatus, newDispatchDate })
                        this.quickRerouteLine = null
                    }
                })
        },

        /**
         * Process a quick reroute for a dispatch request.
         * @param {Object} options - The options for the quick reroute.
         * @param {Object} options.originalDispatchRequest - The original dispatch request to be rerouted.
         * @param {string|undefined} options.toStatus - The status to which the dispatch request should be changed.
         * @param {Date|false} [options.newDispatchDate=false] - The new dispatch date. If false, the original dispatch date will be used.
         * @param {string} [options.responsibleLocationId] - The ID of the responsible location.
         */
        processQuickReroute({ originalDispatchRequest, toStatus, newDispatchDate = false, responsibleLocationId }) {
            this.isSaving = true

            if (newDispatchDate && newDispatchDate < this.today) {
                this.$alert('De uitleverdatum mag niet in het verleden liggen.', 'danger')
                return
            }

            let postData = {}

            if (toStatus) {
                postData.status = toStatus
            }
            if (responsibleLocationId) {
                postData.responsible_location_id = responsibleLocationId
            }
            if (newDispatchDate) {
                postData.dispatch_date = newDispatchDate
            }

            let actionTitle = ''
            switch (toStatus) {
                case 'cancelled':
                    actionTitle = 'annulering'
                    break
                case 'unable-to-ship':
                    actionTitle = 'manco melding'
                    break
                case 'on-hold':
                    actionTitle = 'blokkering'
                    break
                default:
                    actionTitle = 'wijziging'
                    break
            }

            this.$http.patch(`/api/rest/dispatch-requests/id:${originalDispatchRequest.id}`, postData)
                .then(response => {
                    this.$alert(`De ${actionTitle} is succesvol verwerkt.`)
                    this.$emit('reroutingSuccessful')
                })
                .catch(e => {
                    this.$alert(`Fout bij het verwerken van de ${actionTitle}.`, 'danger')
                })
                .then(() => {
                    this.isSaving = false
                })
        },

        /**
         * Process a quick reroute for a dispatch request.
         * @param {Object}     options                                        - The options for the quick reroute.
         * @param {Object}     options.originalDispatchRequest                - The original dispatch request to be rerouted.
         * @param {Array}      [options.lines]                                - The new lines. If not provided, the original lines will be used.
         * @param {string}     [options.toStatus]                             - The status to which the dispatch request should be changed.
         * @param {Date|false} [options.newDispatchDate=false]                - The new dispatch date. If false, the original dispatch date will be used.
         * @param {string}     [options.responsible_location_id]              - The ID of the responsible location.
         *
         * @param {Object}     [options.newLocation]                          - The new location options. Only used when changing the location (Quick Reroute to Location).
         * @param {string}     options.newLocation.dispatcher_type            - The dispatcher type of the new location. e.g. "store"
         * @param {string}     options.newLocation.dispatcher_identifier      - The dispatcher identifier of the new location. e.g. "2051"
         * @param {Object}     options.newLocation.dispatch_type_identifier   - The dispatch type of the new location. e.g. "click_and_collect"
         * @param {Object}     options.newLocation.carrier                    - The carrier of the new location.
         * @param {Object}     options.newLocation.carrier_method             - The carrier method of the new location.
         */
        processQuickRerouteForLines({ originalDispatchRequest, lines, toStatus, newDispatchDate = false, responsible_location_id, newLocation }) {
            // Check if original dispatch request is provided.
            if (originalDispatchRequest === undefined) {
                this.$alert('Er is een fout opgetreden bij het het uitvoeren van de Quick Reroute. Neem contact op met OmniStock.', 'error')
                throw new Error('processQuickReroute(): No original dispatch request provided.')
            }

            // Check if the (optional) new dispatch date is in the past.
            if (newDispatchDate && newDispatchDate < this.today) {
                this.$alert('De uitleverdatum mag niet in het verleden liggen.', 'danger')
                return
            }

            // Initialize new dispatch request.
            this.newDispatchRequests = this.$set(this, 'newDispatchRequests', [])
            this.addDispatchRequest(this.newDispatchRequest)

            // Copy original dispatch request properties.
            this.newDispatchRequests[0].dispatch_type_identifier  = originalDispatchRequest.dispatch_type.identifier
            this.newDispatchRequests[0].dispatch_request_lines    = originalDispatchRequest.dispatch_request_lines
            this.newDispatchRequests[0].dispatcher_type           = originalDispatchRequest.dispatcher_type
            this.newDispatchRequests[0].dispatcher_identifier     = originalDispatchRequest.dispatcher_identifier
            this.newDispatchRequests[0].carrier_identifier        = originalDispatchRequest.carrier?.identifier
            this.newDispatchRequests[0].carrier_method_identifier = originalDispatchRequest.carrier_method?.identifier
            this.newDispatchRequests[0].status                    = originalDispatchRequest.status

            // Set or replace optional values.
            if ( lines                   !== undefined ) { this.newDispatchRequests[0].dispatch_request_lines = lines }
            if ( toStatus                !== undefined ) { this.newDispatchRequests[0].status = toStatus }
            if ( responsible_location_id !== undefined ) { this.newDispatchRequests[0].responsible_location_id = responsible_location_id }
            if (!newDispatchDate && originalDispatchRequest.dispatch_date < this.today) {
                this.newDispatchRequests[0].dispatch_date = this.today
            } else if (newDispatchDate) {
                this.newDispatchRequests[0].dispatch_date = newDispatchDate
            } else {
                this.newDispatchRequests[0].dispatch_date = originalDispatchRequest.dispatch_date
            }

            if ( newLocation !== undefined ) {
              /**
               * Quick Reroute to Location
               * If the new location is defined, we need to update the dispatch request with the new location.
               */

              this.newDispatchRequests[0].dispatcher_type             = 'store';
              this.newDispatchRequests[0].dispatch_type_identifier    = newLocation.dispatch_type.identifier;
              this.newDispatchRequests[0].dispatcher_identifier       = newLocation.dispatcher_identifier;

              // In case of a drop shipment, the carrier and carrier method are not available.
              this.newDispatchRequests[0].carrier_identifier          = newLocation.carrier        ? newLocation.carrier.identifier        : null;
              this.newDispatchRequests[0].carrier_method              = newLocation.carrier_method ? newLocation.carrier_method.identifier : null;
              this.newDispatchRequests[0].carrier_method_identifier   = newLocation.carrier_method ? newLocation.carrier_method.identifier : null;
            }

            let actionTitle = ''
            switch (toStatus) {
                case 'cancelled':
                    actionTitle = 'annulering'
                    break
                case 'unable-to-ship':
                    actionTitle = 'manco melding'
                    break
                case 'on-hold':
                    actionTitle = 'blokkering'
                    break
                default:
                    actionTitle = 'wijziging'
                    break
            }

            this.save(null, actionTitle)
        },
        isOrderPickByStore(dispatchRequest) {
            return dispatchRequest.dispatcher_type === 'store'
        },
        isOrderPickByStoreScannable(dispatchRequest) {
            return !this.rerouteMode
                && !dispatchRequest.paused_at
                && dispatchRequest.status === 'requested'
                && dispatchRequest.dispatcher_type === 'store'
                && this.hasLocation(dispatchRequest.dispatcher_type, dispatchRequest.dispatcher_identifier)
        },
        newDispatchRequestStatusChanged(dispatchRequest) {
            dispatchRequest.dispatch_type = ''
            dispatchRequest.dispatcher_type = ''
            dispatchRequest.dispatcher_identifier = ''
            dispatchRequest.carrier_identifier = ''
            dispatchRequest.carrier_method_identifier = ''

            switch (dispatchRequest.status) {
                case 'to-be-requested':
                    // TODO
                    break
                default:

            }
        },
        dispatcherChanged(dispatchRequest) {
            this.isLoadingDispatcherTypes = true
            this.dispatcherInfo = []

            dispatchRequest.dispatch_type_identifier = ''

            return this.$store.dispatch('loadDispatchInfoForLocation', {type: dispatchRequest.dispatcher_type, identifier: dispatchRequest.dispatcher_identifier})
                .then(() => {
                    const dispatchInfo = this.dispatchTypesForSelectedDispatcher(dispatchRequest)
                    if (dispatchInfo.length === 0) {
                        this.$alert("Geen actieve dispatch-types gevonden voor geselecteerde locatie", 'danger')
                    }
                    else {
                        dispatchRequest.dispatch_type_identifier = this.dispatchTypesForSelectedDispatcher(dispatchRequest)[0].identifier
                        this.dispatchTypeChanged(dispatchRequest)
                    }

                    this.isLoadingDispatcherTypes = false
                })
        },
        dispatchTypeChanged(dispatchRequest) {
            dispatchRequest.carrier_identifier = ''
            dispatchRequest.carrier_method_identifier = ''

            const carriers = this.carriersForSelectedDispatcherAndDispatchType(dispatchRequest)
            if (carriers.length === 0) {
                this.$alert('Geen actieve vervoerders gevonden voor geselecteerde dispatch-type', 'danger')
            }
            else if (carriers.length === 1) {
                dispatchRequest.carrier_identifier = carriers[0].identifier
                this.carrierChanged(dispatchRequest)
            }
            else {
                let defaultCarrier = carriers.find(c => c?.carrier_methods.some(m => m.is_default)) ?? null
                if (defaultCarrier) {
                    dispatchRequest.carrier_identifier = defaultCarrier.identifier
                    this.carrierChanged(dispatchRequest)
                }
            }
        },
        carrierChanged(dispatchRequest) {
            dispatchRequest.carrier_method_identifier = ''

            const carrierMethods = this.carrierMethodsForSelectedCarrierAndDispatcherAndDispatchType(dispatchRequest)
            if (carrierMethods.length === 0) {
                this.$alert('Geen actieve vervoersmethodes gevonden voor geselecteerde vervoerder', 'danger')
            }
            else if (carrierMethods.length === 1) {
                dispatchRequest.carrier_method_identifier = carrierMethods[0].identifier
            }
            else {
                let defaultCarrierMethod = carrierMethods.find(m => m.is_default)
                if (defaultCarrierMethod) {
                    dispatchRequest.carrier_method_identifier = defaultCarrierMethod.identifier
                }
            }
        },
        showDrqLineHistory(line) {
            this.dispatchRequestHistoryLine = line
            this.$bvModal.show(`drq-line-history-modal-${line.dispatch_request_id}`);
        },
        openSwitchCarrierModal(dispatchRequest) {
            this.$bvModal.show(`switch-carrier-method-modal-${dispatchRequest.id}`);
        },
        openChooseResponsibleLocationModal(dispatchRequest) {
            this.$bvModal.show(`responsible-location-modal-${dispatchRequest.id}`)
        },
        openChooseDropshipperModal(dispatchRequest) {
            this.$bvModal.show(`choose-dropshipper-modal-${dispatchRequest.id}`)
        },
        responsibleLocationPopupDataChange(responsibleLocationId, dispatcherType, dispatcherIdentifier, dispatchType, carrier, carrierMethod) {
            this.responsibleLocationChange = {
                responsibleLocationId: responsibleLocationId,
                dispatcherType: dispatcherType,
                dispatcherIdentifier: dispatcherIdentifier,
                dispatchType: dispatchType,
                carrier: carrier,
                carrierMethod: carrierMethod,
            }
        },
        chooseDropshipperPopupDataChange(dispatcherType, dispatcherIdentifier, dispatchType, carrier, carrierMethod, selectedDeliveryDate) {
            this.chooseDropshipper = {
                dispatcherType: dispatcherType,
                dispatcherIdentifier: dispatcherIdentifier,
                dispatchType: dispatchType,
                carrier: carrier,
                carrierMethod: carrierMethod,
                deliveryDate: selectedDeliveryDate
            }
        },
        switchCarrierDataChange(carrierMethodId) {
            this.switchCarrier = {
                carrierMethodId: carrierMethodId,
            }
        },
        async handleProcessResponsibleLocation(dispatchRequest) {
            let newDrq = this.addDispatchRequest(dispatchRequest)
            newDrq.responsible_location_id = this.responsibleLocationChange.responsibleLocationId
            newDrq.dispatcher_type = this.responsibleLocationChange.dispatcherType
            newDrq.dispatcher_identifier = this.responsibleLocationChange.dispatcherIdentifier
            newDrq.dispatch_type_identifier = this.responsibleLocationChange.dispatchType
            newDrq.carrier_identifier = this.responsibleLocationChange.carrier
            newDrq.carrier_method_identifier = this.responsibleLocationChange.carrierMethod
            newDrq.dispatch_request_lines = _.cloneDeep(dispatchRequest.dispatch_request_lines)
            if (newDrq.dispatch_date < this.today) {
                newDrq.dispatch_date = this.today
            }

            this.save(null, null)
                .then(async () => {
                    this.$alert(this.$t('omt.gui.orders.choose-responsible-location.success-message'))

                    if (!this.hasLocation(newDrq.dispatcher_type_identifier, newDrq.dispatcher_identifier) && !this.hasLocationId(newDrq.responsible_location_id)) {
                        await this.$router.push({
                            name: 'FrontendFilterOrderSearch'
                        })
                    }
                })
                .catch(e => {
                    this.$alert(this.$t('omt.gui.orders.choose-responsible-location.error-message'), 'danger')
                })
                .finally(() => {
                    this.$bvModal.hide(`responsible-location-modal-${dispatchRequest.id}`)

                    this.responsibleLocationChange = {
                        responsibleLocationId: null,
                        dispatcherType: null,
                        dispatcherIdentifier: null,
                        dispatchType: null,
                        carrier: null,
                        carrierMethod: null,
                    }
                })
        },
        handleProcessChooseDropshipper(dispatchRequest) {
            let newDrq = this.addDispatchRequest(dispatchRequest)
            newDrq.status = 'to-be-requested'
            newDrq.dispatcher_type = this.chooseDropshipper.dispatcherType
            newDrq.dispatcher_identifier = this.chooseDropshipper.dispatcherIdentifier
            newDrq.dispatch_type_identifier = this.chooseDropshipper.dispatchType
            newDrq.carrier_identifier = this.chooseDropshipper.carrier
            newDrq.carrier_method_identifier = this.chooseDropshipper.carrierMethod
            newDrq.dispatch_request_lines = _.cloneDeep(dispatchRequest.dispatch_request_lines)

            newDrq.delivery_date = this.chooseDropshipper.deliveryDate
            if (newDrq.dispatch_date < this.today) {
                newDrq.dispatch_date = this.today
            }

            this.save()

            this.$bvModal.hide(`choose-dropshipper-modal-${dispatchRequest.id}`)
            this.chooseDropshipper = {
                locationId: null,
                dispatcherType: null,
                dispatcherIdentifier: null,
                dispatchType: null,
                carrier: null,
                carrierMethod: null,
            }
        },
        handleSwitchCarrier(dispatchRequest) {
            let newDrq = this.addDispatchRequest(dispatchRequest)
            newDrq.status = dispatchRequest.status
            newDrq.dispatcher_type = dispatchRequest.dispatcher_type
            newDrq.dispatcher_identifier = dispatchRequest.dispatcher_identifier
            newDrq.dispatch_type_identifier = dispatchRequest.dispatch_type.identifier
            newDrq.carrier_method_id = this.switchCarrier.carrierMethodId
            newDrq.dispatch_request_lines = _.cloneDeep(dispatchRequest.dispatch_request_lines)
            if (newDrq.dispatch_date < this.today) {
                newDrq.dispatch_date = this.today
            }

            delete newDrq.carrier_method_identifier
            delete newDrq.carrier_identifier

            this.save()

            this.$bvModal.hide(`switch-carrier-method-modal-${dispatchRequest.id}`)
            this.switchCarrier = {
                carrierMethodId: null,
            }
        },

        getActiveCarriersForDispatchType(dispatchType) {
          if (!dispatchType.is_active || !dispatchType.is_active_for_location) {
            return [];
          }

          return dispatchType.carriers;
        },

        dispatchTypeHasActiveCarriers(dispatchType) {
          return this.getActiveCarriersForDispatchType(dispatchType).length !== 0
        },

        getActiveCarrierMethodsForCarrier(carrier) {
          return carrier?.carrier_methods.filter(cm => cm.is_active && cm.is_active_for_location)
        },

        carrierHasActiveCarrierMethods(carrier) {
          return this.getActiveCarrierMethodsForCarrier(carrier).length > 0
        },

        /**
         * Gets the dispatch types for the given dispatcher and saves them to this.loadingQuickRerouteDispatchTypes
         * @param {string} dispatcherIdentifier - The identifier of the dispatcher.
         */
        getDispatchTypesForDispatcher(dispatcherIdentifier) {
          if (dispatcherIdentifier === null) {
            return
          }

          const endpoint = `/api/rest/locations/store/${dispatcherIdentifier}/dispatch-types`

          this.loadingQuickRerouteDispatchTypes = true;

          // Reset dispatchType, carrier and carrierMethod.
          this.newDispatchRequestLocation.dispatch_type              = null;
          this.newDispatchRequestLocation.carrier                    = null;
          this.newDispatchRequestLocation.carrier_method             = null;

          this.$http.get(endpoint)
            .then((response) => {
              this.quickRerouteLocationDispatchTypes = response.data.filter(item => item.is_active_for_location && item.identifier !== 'vendor_dropship')
                .map(item => {
                  const carriers = item.carriers.reduce((acc, carrier) => {
                        const carrierMethods = carrier?.carrier_methods.filter(method => method.is_active_for_location);
                        if (carrierMethods.length > 0) {
                            acc.push({ ...carrier, carrier_methods: carrierMethods });
                        }
                        return acc;
                  }, []);
                  return { ...item, carriers };
                })
                .filter(item => item.carriers.length > 0);
            })
            .catch((error) => {
              this.$alert('Er is iets fout gegaan bij het ophalen van de dispatch types', 'error');
              console.error(`Error while fetching dispatch types for location ${dispatcherIdentifier}: ${error}`);
            })
            .finally(() => {
              this.loadingQuickRerouteDispatchTypes = false;
            });
        },

        canQuickRerouteToLocation(dispatchRequest) {
          return !this.rerouteMode
                && this.hasPermission('orders.dispatchrequests.quick-reroute.location')
                && dispatchRequest.dispatcher_type === 'dc' ;
        },

        openCascadedDispatchRequestsModal(dispatchRequest) {
            const cascadedIds = dispatchRequest.cascades_into_dispatch_requests.map(dr => dr.id)
            this.showCascadedDispatchRequests = this.dispatchRequests.filter(dr => cascadedIds.indexOf(dr.id) !== -1)
            this.$bvModal.show('cascaded-drqs-modal')
        },

        openReturnDrqItemsModal(dispatchRequest) {
            this.returnDrq = structuredClone(dispatchRequest);
            this.$bvModal.show('return-drq-items-modal');
        },

        toggleSuggestionForDispatchRequestModal(dispatchRequest) {
            dispatchRequest.isSuggestionModalOpen = true;
        },

        getArticleHoverCardVariables(salesOrderLine) {
            const { product_identifier } = salesOrderLine;

            return {
                article_no: product_identifier ?? undefined,
            };
        },

        showLocationLink(locationType, locationIdentifier) {
            return this.$store.state.config.settings['frontend-parameters.dispatch-requests']['enable-location-links']
                && this.hasAnyPermission(['locations.services.view', 'locations.services.edit'])
                && this.hasLocation(locationType, locationIdentifier)
        },

        /**
         * Creates a batch for the dispatch requests.
         *
         * @param {Object} params - The parameters for creating a batch.
         * @param {string} [params.batch_type="default"] - The type of the batch to be created. Defaults to "default".
         * @param {string[]} params.drqIdentifiers - An array of dispatch request identifiers.
         * @param {string} params.locationTypeIdentifier - The identifier for the location type.
         * @param {string} params.locationIdentifier - The identifier for the specific location.
         */
        createBatch( { batch_type = "default", drqIdentifiers, locationTypeIdentifier, locationIdentifier } ) {
            this.isCreatingBatch = true

            this.$http.post(`/api/rest/locations/${locationTypeIdentifier}/${locationIdentifier}/dispatch-request-batches`, {
                dispatch_requests: drqIdentifiers,
                batch_type,
            })
            .then(response => {
                const batchIdentifier = response.data.identifier;
                this.$alert(`Batch succesvol aangemaakt.`, 'success')
                this.$router.push({
                    name: 'ShipmentBatchView',
                    params: {
                        locationTypeIdentifier,
                        locationIdentifier,
                        batchIdentifier
                    },
                })
            })
            .catch(error => {
                console.error(`Error in method createBatch`, error);
                this.$alert(`Fout bij het aanmaken van de batch.\nProbeer het opnieuw of neem contact op met OmniStock.`, "danger")
            })
            .finally(() => {
                this.isCreatingBatch = false;
            })
        },

        /**
         * Handles the creation of a batch for a specific dispatch request.
         *
         * @param {Object} dispatchRequest - The dispatch request object.
         */
        handleCreateBatch(dispatchRequest) {
            const createBatchParams = {
                drqIdentifiers:         [dispatchRequest.identifier],
                locationTypeIdentifier: dispatchRequest.dispatcher_type,
                locationIdentifier:     dispatchRequest.dispatcher_identifier,
            }

            this.createBatch(createBatchParams);
        },

        showDrqInformationBlock(identifier) {
            return this.$store.getters.getFrontendParameterValue('order-details', 'drq-information-blocks').includes(identifier) || false;
        },

        getDispatchRequestLineDiscountAmount(dispatchRequestLine) {
            let salesOrderLine = this.getSalesOrderLine(dispatchRequestLine.sales_order_line_id)
            return (salesOrderLine.discount_amount / salesOrderLine.quantity) * dispatchRequestLine.quantity
        },

        async processPauseDispatchRequest(dispatchRequest) {
            return this.$http.post(`/api/rest/dispatch-requests/id:${dispatchRequest.id}/pause`)
                .then(() => {
                    this.$alert("Levering gepauzeerd")
                    this.$emit('dataChanged')
                })
                .catch(e => {
                    console.error(e)
                    this.$alert("Fout bij het pauzeren van de levering", "danger")
                })
        },

        async processResumeDispatchRequest(dispatchRequest) {
            return this.$http.post(`/api/rest/dispatch-requests/id:${dispatchRequest.id}/resume`)
                .then(() => {
                    this.$alert("Levering hervat")
                    this.$emit('dataChanged')
                })
                .catch(e => {
                    console.error(e)
                    this.$alert("Fout bij het hervatten van de levering", "danger")
                })
        },

        async processOverridePickupCode(dispatchRequest) {
            return this.$http.post(`/api/rest/dispatch-requests/id:${dispatchRequest.id}/override-pickup-code`)
                .then(() => {
                    this.$alert("Levering gemarkeerd als afgehaald")
                    this.$emit('dataChanged')
                })
                .catch(e => {
                    console.error(e)
                    this.$alert("Fout bij het markeren van de levering", "danger")
                })
        },

        getTotalPriceToShow(line, includeDiscount) {
            let salesOrderLine = this.getSalesOrderLine(line.sales_order_line_id)

            if (this.hasOriginalExtraOrderLineUnitPrice(line)) {
                return salesOrderLine.metadata.extra_order_line_unit_price_to_show * line.quantity
            }

            return includeDiscount
                ? (
                    (
                        salesOrderLine.original_line_gross_price - (salesOrderLine.discount_amount ?? 0))
                        / salesOrderLine.quantity
                    )
                    * line.quantity
                : (salesOrderLine.original_line_gross_price / salesOrderLine.quantity) * line.quantity
        },

        getTotalPriceToShowForDrq(lines) {
            return _.chain(lines)
                .map(line => this.getTotalPriceToShow(line, true))
                .sum()
                .value()
        },

        getTotalQuantityForDrq(lines) {
            return _.sumBy(lines, 'quantity')
        },

        getDiscountPriceToShow(line) {
          let salesOrderLine = this.getSalesOrderLine(line.sales_order_line_id)
          return (salesOrderLine.discount_amount / salesOrderLine.quantity) * line.quantity
        },

        hasOriginalExtraOrderLineUnitPrice(line) {
            let salesOrderLine = this.getSalesOrderLine(line.sales_order_line_id)
            return this.hasPermission('extraorderlines.showmetadataprice')
                && salesOrderLine.is_extra
                && (salesOrderLine.metadata?.extra_order_line_unit_price_to_show ?? 0)
        },

        getGoodsReceiptViewLink(goodsReceipt) {
            const location = getLocationForUserById(goodsReceipt.recipient_location_id)

            // You can only go to goods receipts of locations you're linked to
            if (!location) {
                return null
            }

            return {
                name: 'GoodsReceiptView',
                params: {
                    locationTypeIdentifier: location.location_type.identifier,
                    locationIdentifier: location.identifier,
                    goodsReceiptIdentifier: goodsReceipt.identifier,
                },
            }
        },
        hasAssignMessages(dispatchRequest) {
            const HARR_REASONS_STATUSES = [
                'unable-to-ship',
                'unable-to-assign',
                'waiting-for-customer-decision',
            ]

            return !!(dispatchRequest.is_dispatch_assign_retryable !== null
                && HARR_REASONS_STATUSES.includes(dispatchRequest.status)
                && (
                    this.relevantAssignMessages(dispatchRequest).length
                    || this.getUnableToShipReasonForHarr(dispatchRequest)
                )
            )
        },
        getUnableToShipReasonForHarr(dispatchRequest) {
            return dispatchRequest.metadata?.unable_to_ship_reason
        },
        relevantAssignMessages(dispatchRequest) {
            const messages = dispatchRequest.metadata
                ?.assign_component_results
                ?.filter(componentResult =>
                    (componentResult.component_identifier === "App\\Components\\Omt\\DispatchAssigner\\Sle" && componentResult.is_error)
                    ||
                    (componentResult.component_identifier === "App\\Components\\Omt\\DispatchAssigner\\RerouteComponents\\RerouteClickAndCollectUnableToShip" && componentResult.message_identifier === "former_pickup")
                )
                ?? []

            return _.uniqBy(messages, (message) => `${message.component_identifier}|${message.message_identifier}`)
        },
        getAssignComponentResultMessage(assignComponentResult) {
          return this.$t(`omt.gui.orders.dispatch-requests.dispatch-component-result-messages.${assignComponentResult.message_identifier}`)
        },
        shouldShowProductInsufficientStock(dispatchRequest, productIdentifier) {
            if (!(this.$store.getters.getFrontendParameterValue('order-details', 'drq-show-insufficient-stock-message') ?? true)) {
              return false;
            }

            return dispatchRequest
                ?.metadata
                ?.assign_component_results
                ?.some(componentResult => componentResult.message_identifier === 'insufficient_stock' && componentResult.product_identifier === productIdentifier)
                ?? false
        },

        isExtraLabelButtonVisible(dispatchRequest) {
            if (!this.showDispatchRequestDeliveryFields(dispatchRequest)) {
                return false
            }

            if (this.hasPermission('orders.dispatchrequests.requested.extralabels') && dispatchRequest.status === 'requested') {
                return true
            }

            if (this.hasPermission('orders.dispatchrequests.shipped.extralabels') && dispatchRequest.status === 'shipped') {
                return true
            }

            return false
        },
        showBoxSuggestion(dispatchRequest) {
          return dispatchRequest.box_suggestion && this.hasPermission('orders.dispatchrequests.boxsuggestions');
        },

        getStrikeThrough(dispatchRequest) {
            return (dispatchRequest.is_replaced && !this.showReplacedAsNormal)
                || (
                    dispatchRequest.status === 'cancelled' && this.$store.getters.getFrontendParameterValue('order-details', 'cancelled-orders.strikethrough')
                )
        },
        isRerouted(dispatchRequest) {
            return dispatchRequest.dispatch_type?.identifier !== 'vendor_dropship'
                && !hasLocation(dispatchRequest.dispatcher_type, dispatchRequest.dispatcher_identifier)
        },
        isResponsibleLocationChanged(dispatchRequest) {
            // Use .responsible_location_id rather than .responsible_location?.id because the `responsible_location` relation is not always loaded.
            return dispatchRequest.is_replaced
                && dispatchRequest.responsible_location_id !== null
                && dispatchRequest.responsible_location_id !== this.currentResponsibleLocationId()
        },
        currentResponsibleLocationId() {
            // Use .responsible_location_id rather than .responsible_location?.id because the `responsible_location` relation is not always loaded.
            // Note: assumes that if there are multiple non-replaced DRQs, then they all must belong to the same responsible location.
            return this.currentDispatchRequests.filter(drq => !drq.is_replaced)[0]?.responsible_location_id ?? null
        }
    },

    watch: {
        newDispatchRequests: {
            deep: true,
            handler(newVal) {
                _.each(newVal, dr => {
                    _.each(dr.dispatch_request_lines, line => {
                        try {
                            line.gui.isInvalidlyPlaced = this.$store.state.dispatchRequestAllowedStatusChanges[line.gui.originalStatus].indexOf(dr.status) === -1
                            if (line.gui.isInvalidlyPlaced) {
                                const allowedStatusesDisplayNames = this.$store.state.dispatchRequestAllowedStatusChanges[line.gui.originalStatus]
                                    .map(status => this.$options.filters.dispatchRequestStatus(status))

                                line.gui.isInvalidlyPlacedReason = `Deze orderregel komt van een levering met status '${this.$options.filters.dispatchRequestStatus(line.gui.originalStatus)}' `
                                    +`en mag enkel worden verplaatst naar leveringen met een van de volgende statussen: ${allowedStatusesDisplayNames.map(s => `'${s}'`).join(', ')}.`
                            }
                            else {
                                line.gui.isInvalidlyPlacedReason = ''
                            }
                        } catch (e) {
                            line.gui.isInvalidlyPlaced = true
                            line.gui.isInvalidlyPlacedReason = `Deze orderregel komt van een levering met status '${this.$options.filters.dispatchRequestStatus(line.gui.originalStatus)}'. `
                                +`Deze status is niet geconfigureerd voor rerouting. Neem contact op met OmniStock als u denkt dat dit onjuist is.`
                        }
                    })
                })
            }
        },
    },
}
</script>

<style lang="scss">
.dispatchRequest {
    .card {
        position: static !important; /* Fix for z-index problem when dragging items into other cards */
    }

    .drag-not-allowed {
        cursor: not-allowed;
    }

    .draggable {
        cursor: move;
    }

    .sortable-drag {
        cursor: grabbing;
    }

    .cursor-default {
        cursor: default;
    }

    .input-not-valid {
        border: solid 1px red;
    }

    .dispatchRequest {
        h4 {
            margin-bottom: 0;

            > small {
                font-size: 1rem;
            }
        }
    }

    .carrier-icon {
        max-width: 50px;
        max-height: 14px;
        filter: grayscale(0.2);

        &:hover {
            filter: grayscale(0) !important;
        }
    }

    .drq-info-heading {
        color: #0d1b3eb3;
    }
}

.promotion-popover {
    max-width: 1000px;
}
</style>
